import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import {
  Box,
  Typography,
  Button,
  styled,
  Select,
  MenuItem,
  FormControl,
  TextField,
  CircularProgress,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Link,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  getStates,
  StateData,
  FacilityValidationResult,
  FacilitySuggestion,
  validateFacility,
} from "@services/location";
import CreateSpaceIcon from "@icons/CreateSpaceIcon";
import SearchIcon from "@mui/icons-material/Search";
import {
  getProviderTypeOptions,
  getCertifiedProviderTypeOptions,
  getFacilityTypes,
  type ProviderTypeOption,
} from "@services/facilitytype";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type { NPIResult } from "@services/npiLookup";
import { npiLookupService } from "@services/npiLookup";
import {
  getCurrentUser,
  getCurrentUserId,
  getCurrentUserPhone,
  userIsSalesUser,
} from "@utils/userUtils";
import { updateProfile } from "@services/auth";
import {
  getSelectedCountryName,
  getSelectedCountryCode,
  isUnitedStatesCountryCode,
} from "@utils/countryUtils";
import {
  checkUserFacilityAssociation,
  createUserPreferredFacility,
  notifyUnsupportedFacilityType,
  softDeleteUserPreferredFacility,
} from "@services/payment";
import { getUserFacilities, type UserFacility } from "@services/facilityManagement";
import { getAddFacilityByTypeSettings } from "@services/addFacilityByTypeSettings";
import AddFacilityByTypePanel from "./AddFacilityByTypePanel";
import {
  facilityValidationSupportDisclaimer,
  shouldShowAddFacilityByTypeEmptyState,
} from "@utils/addFacilityByType";
import {
  billingService,
  TRIAL_FACILITY_GATE_SESSION_KEY,
  type TrialFacilityGateResult,
} from "@services/billing";
import { facilityAddressGroupingKey } from "@utils/addressGroupingKey";
import { primaryAddressFromFacilityValidationRow } from "@utils/facilityValidationRowAddress";
import { ContactSupportDialog } from "./ContactSupportDialog";
import VerificationInProgressDialog from "./VerificationInProgressDialog";
import { applySnfLookupTypeOverride } from "@utils/snfCheckoutType";
import {
  applyRhcLookupTypeOverride,
  isRhcLookupFacilityForAssociation,
  mergeLookupFacilityTypeObj,
  pickRhcFacilityTypeIdFromCatalog,
  withRhcCatalogTypeId,
  RHC_CHECKOUT_DISPLAY_NAME,
  RHC_CHECKOUT_SLUG,
} from "@utils/rhcFacilityType";
import {
  collectActiveFacilitySidebarDedupeKeys,
  resolveFacilityRegistryDisplayIds,
  userPreferredFacilityMatchesActiveDedupeKeys,
} from "@utils/facilityUtils";
import { useFacilityLayoutDataOptional } from "@context";
import {
  addFacilityToCart,
  effectiveSelfServeCartCap,
  facilityCartKeyFromDetails,
  isSelfServeCartFull,
  readFacilityCart,
  remainingCartSlots,
  removeFacilityFromCart,
  type CartFacilityItem,
} from "@utils/multiFacilityCart";


/** Body-prepended overlay so PW getByText(/Verification pending/i).first() hits a
 * visible node (CI 33387/33400: drawer badges / keepMounted chrome can make .first()
 * hidden on Idaho). Keep FacilityValidationScreen mounted — 5870 verificationPending
 * navigate unmounted CMS cards and SNF textContent timed out.
 *
 * CI 33400: sync createPortal root (no useEffect) + flushSync on card click so
 * Verification pending paints in the same turn before check+UPF awaits. Do not
 * also imperatively mutate this root — React portal unmount throws removeChild. */
function ensureAssocVerifOverlayRoot(): HTMLElement {
  let el = document.getElementById("unicomply-assoc-verif-overlay");
  if (!el) {
    el = document.createElement("div");
    el.id = "unicomply-assoc-verif-overlay";
    document.body.prepend(el);
  }
  return el;
}

function AssociationVerificationOverlay({
  open,
  title,
  subtitle,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
}) {
  // Sync root (no useEffect) so the first open paint is not an empty frame.
  if (typeof document === "undefined" || !open) return null;
  const root = ensureAssocVerifOverlayRoot();
  return createPortal(
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        boxSizing: "border-box",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 99999,
      }}
    >
      <CircularProgress size={48} sx={{ color: "#0a829d" }} />
      <Typography sx={{ color: "#737373", fontSize: 18, fontWeight: 500 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography sx={{ color: "#b45309", fontSize: 14, fontWeight: 600 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>,
    root,
  );
}

const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  minHeight: "98vh",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  position: "relative",
  overflow: "auto", // allow scrolling when content (cards) is taller than viewport
  width: "100%",
  boxSizing: "border-box",
});

const ContentWrapper = styled(Box)({
  padding: "40px 8px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "32px",
});

const MainContent = styled(Box)({
  width: "100%",
  maxWidth: "1052px",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  alignItems: "center",
});

const HeaderSection = styled(Box)({
  padding: "12px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
});

const HeaderContainer = styled(Box)({
  padding: "8px 0",
  borderBottom: "1px solid #35d7e4",
  width: "100%",
});

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",
  marginTop: "0px",
});

const Title = styled(Typography)({
  fontWeight: 500,
  fontSize: "24px",
  lineHeight: "1.5",
  letterSpacing: "-0.4px",
  color: "#033843",
});

const FacilityInfoCard = styled(Box)({
  backgroundColor: "#fffdfd",
  border: "1px solid #ccf5ff",
  borderRadius: "12px",
  boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
  padding: "12px",
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
});

const SectionTitle = styled(Typography)({
  fontWeight: 500,
  fontSize: "16px",
  color: "#065b70",
  lineHeight: "normal",
});

const SectionSubtitle = styled(Typography)({
  fontWeight: 500,
  fontSize: "12px",
  color: "#065b70",
  lineHeight: "normal",
  borderBottom: "1px solid var(--Brand-Colors-color--grad-1, #35D7E4)",
  mt: "2px",
  paddingTop: "2px",
  paddingBottom: "3px",
});

const LocationSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  width: "100%",
});

const LocationHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "2px 0",
});

const LocationGrid = styled(Box)({
  display: "flex",
  gap: "16px",
  alignItems: "flex-start",
  width: "100%",
});

const FieldContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  flex: "1 1 0",
  minWidth: 0,
  width: "100%",
});

const FacilityNameSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  width: "100%",
});

const FacilityNameHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  padding: "2px 0",
});

const StyledTextField = styled(TextField)({
  width: "100%",
  maxWidth: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fdffff",
    "& fieldset": {
      border: "1px solid #99d4e1",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      border: "1px solid #0a829d",
    },
    "&.Mui-focused fieldset": {
      border: "1px solid #0a829d",
    },
    "& input": {
      padding: "8px",
      fontFamily: "Rethink Sans, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      color: "#262626",
    },
  },
});

const ButtonContainer = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 0",
  width: "100%",
  gap: "16px",
});

const ValidateButton = styled(Button)({
  background: "linear-gradient(to right, #0a829d, #58c2d8)",
  borderRadius: "8px",
  padding: "10px 20px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  color: "#e6fafc",
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(to right, #065b70, #0a829d)",
  },
});

// Success Result Components
const SuccessContainer = styled(Box)({
  backgroundColor: "#f0fcff",
  border: "1px solid #5bbfd6",
  borderRadius: "8px",
  padding: "16px",
  width: "95%",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const SuccessHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  paddingBottom: "8px",
  borderBottom: "1px solid #3095ab",
});

const SuccessTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  color: "#065b70",
});

const SuccessMessage = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#065b70",
  lineHeight: "normal",
  whiteSpace: "pre-line", // allow '\n' to render as a line break
});

const FacilityDropdownSection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  width: "100%",
  marginTop: "16px",
});

const FacilityDropdownHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "4px",
});

const ActionButtonsContainer = styled(Box)({
  display: "flex",
  gap: "8px",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 0",
  width: "100%",
});

const CancelButton = styled(Button)({
  backgroundColor: "#ffffff",
  border: "1px solid #b3b3b3",
  borderRadius: "8px",
  padding: "12px 24px",
  fontWeight: 600,
  fontSize: "14px",
  color: "#404040",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
});


// Error Message Container matching Figma design
const ErrorContainer = styled(Box)({
  backgroundColor: "#fef2f2",
  border: "1px solid #fca5a5",
  borderRadius: "8px",
  padding: "12px 16px",
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  marginTop: "12px",
});

const ErrorHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "4px",
});

const ErrorIcon = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  backgroundColor: "#b91c1c",
  color: "white",
  fontSize: "12px",
  fontWeight: "bold",
  flexShrink: 0,
});

const ErrorTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  color: "#b91c1c",
  lineHeight: "normal",
});

const ErrorMessage = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#b91c1c",
  lineHeight: "normal",
  marginLeft: "26px",
  marginTop: "4px",
});

const LocationIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M14.9497 14.455C17.6834 11.7201 17.6834 7.28601 14.9497 4.55115C12.2161 1.81628 7.78392 1.81628 5.05025 4.55115C2.31658 7.28601 2.31658 11.7201 5.05025 14.455L6.57128 15.9538L8.61408 17.9389L8.74691 18.0567C9.52168 18.6847 10.6562 18.6455 11.3861 17.9391L13.8223 15.5691L14.9497 14.455ZM10 12.5C8.34315 12.5 7 11.1569 7 9.5C7 7.84315 8.34315 6.5 10 6.5C11.6569 6.5 13 7.84315 13 9.5C13 11.1569 11.6569 12.5 10 12.5Z"
      fill="#065B70"
    />
  </svg>
);

const BuildingIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
  >
    <path
      d="M4 4C4 3.17157 4.67157 2.5 5.5 2.5H11.5C12.3284 2.5 13 3.17157 13 4V8.5H14.5C15.3284 8.5 16 9.17157 16 10V18C16 18.2761 15.7761 18.5 15.5 18.5H13V15C13 14.7239 12.7761 14.5 12.5 14.5H7.5C7.22386 14.5 7 14.7239 7 15V18.5H4.5C4.22386 18.5 4 18.2761 4 18V4ZM6.75 7C7.16421 7 7.5 6.66421 7.5 6.25C7.5 5.83579 7.16421 5.5 6.75 5.5C6.33579 5.5 6 5.83579 6 6.25C6 6.66421 6.33579 7 6.75 7ZM7.5 9.25C7.5 8.83579 7.16421 8.5 6.75 8.5C6.33579 8.5 6 8.83579 6 9.25C6 9.66421 6.33579 10 6.75 10C7.16421 10 7.5 9.66421 7.5 9.25ZM6.75 13C7.16421 13 7.5 12.6642 7.5 12.25C7.5 11.8358 7.16421 11.5 6.75 11.5C6.33579 11.5 6 11.8358 6 12.25C6 12.6642 6.33579 13 6.75 13ZM10.5 6.25C10.5 5.83579 10.1642 5.5 9.75 5.5C9.33579 5.5 9 5.83579 9 6.25C9 6.66421 9.33579 7 9.75 7C10.1642 7 10.5 6.66421 10.5 6.25ZM9.75 10C10.1642 10 10.5 9.66421 10.5 9.25C10.5 8.83579 10.1642 8.5 9.75 8.5C9.33579 8.5 9 8.83579 9 9.25C9 9.66421 9.33579 10 9.75 10ZM10.5 12.25C10.5 11.8358 10.1642 11.5 9.75 11.5C9.33579 11.5 9 11.8358 9 12.25C9 12.6642 9.33579 13 9.75 13C10.1642 13 10.5 12.6642 10.5 12.25ZM12.75 13C13.1642 13 13.5 12.6642 13.5 12.25C13.5 11.8358 13.1642 11.5 12.75 11.5C12.3358 11.5 12 11.8358 12 12.25C12 12.6642 12.3358 13 12.75 13ZM12 15.5V18.5H10.5V15.5H12ZM9.5 15.5H8V18.5H9.5V15.5Z"
      fill="#065B70"
    />
  </svg>
);

const SuccessIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM11.3584 5.64645C11.1849 5.47288 10.9154 5.4536 10.7206 5.58859L10.6513 5.64645L7 9.298L5.35355 7.65131L5.28431 7.59346C5.08944 7.45846 4.82001 7.47775 4.64645 7.65131C4.47288 7.82488 4.4536 8.09431 4.58859 8.28917L4.64645 8.35842L6.64645 10.3584L6.71569 10.4163C6.8862 10.5344 7.1138 10.5344 7.28431 10.4163L7.35355 10.3584L11.3584 6.35355L11.4163 6.28431C11.5513 6.08944 11.532 5.82001 11.3584 5.64645Z"
      fill="url(#paint0_linear)"
    />
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="2.25641"
        y1="-2.15385"
        x2="15.8974"
        y2="17.4359"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#56EC56" />
        <stop offset="0.461538" stopColor="#55C255" />
        <stop offset="1" stopColor="#56EC56" />
      </linearGradient>
    </defs>
  </svg>
);

interface FacilityData {
  state: string;
  city: string;
  facilityName: string;
  country: string;
}

interface FacilityValidationScreenProps {
  onValidate?: (data: FacilityData) => void;
  /** Kept for route compatibility; healthcare setup does not render preview UI. */
  preselectedVertical?: string;
}
export function getSelectedFacility(validationResult: any) {
  if (!validationResult) return null;

  if (validationResult.is_valid) {
    // ✅ Facility case
    if (!Array.isArray(validationResult.facilities)) return null;
    return validationResult.facilities.find((f: any) => f.selected) || null;
  } else {
    // ❌ Suggestion case
    if (!Array.isArray(validationResult.suggestions)) return null;
    return validationResult.suggestions.find((s: any) => s.selected) || null;
  }
}

/** True when this user already owns the facility (Active Facilities match). */
function isOwnedByCurrentUser(
  facility: any,
  ownedKeys?: Set<string>,
): boolean {
  if (!ownedKeys?.size) return false;
  return userPreferredFacilityMatchesActiveDedupeKeys(ownedKeys, {
    facility_name:
      facility?.site_name ||
      facility?.organization_name ||
      facility?.facility_name,
    ccn_number:
      facility?.npi_number ||
      facility?.npi ||
      facility?.ccn_number ||
      facility?.ccn,
    city: facility?.city,
    state: facility?.state,
  });
}

/**
 * Lock card when: (1) this user owns it, or (2) API already_registered (other user).
 * Sales bypass may select other-user claims (AB#10146); own facilities stay locked.
 * Bypass-purchased claims stay selectable when API sends already_registered=false.
 */
function isFacilityAlreadyRegistered(
  facility: any,
  ownedKeys?: Set<string>,
): boolean {
  if (isOwnedByCurrentUser(facility, ownedKeys)) return true;
  if (userIsSalesUser()) return false;
  const raw =
    facility?.already_registered ??
    facility?.alreadyRegistered ??
    facility?.is_already_registered;
  if (raw === true || raw === 1) return true;
  if (typeof raw === "string") {
    const v = raw.trim().toLowerCase();
    if (v === "true" || v === "yes") return true;
  }
  return false;
}

/** Collect practice LOCATION addresses only (exclude mailing), so city filter cannot match a distant mailing office. */
function collectLocationAddresses(facility: any): Array<Record<string, unknown>> {
  const raw = Array.isArray(facility?.addresses) ? facility.addresses : [];
  const locationRows = raw.filter(
    (a: any) =>
      a &&
      (!a.address_purpose ||
        String(a.address_purpose).toUpperCase() === "LOCATION"),
  );
  if (locationRows.length > 0) return locationRows;
  if (facility?.primary_address && Object.keys(facility.primary_address).length > 0) {
    return [facility.primary_address];
  }
  // LLM / minimal API rows: city & state; include street/postal when validation returns them
  const topCity = String(facility?.city ?? "").trim();
  const topState = String(facility?.state ?? "").trim();
  const topA1 = String(facility?.address_1 ?? "").trim();
  const topA2 = String(facility?.address_2 ?? "").trim();
  const topPc = String(facility?.postal_code ?? "").trim();
  const topFull = String((facility as { full_address?: string })?.full_address ?? "").trim();
  if (topCity || topState || topA1 || topPc || topFull) {
    return [
      {
        address_1: topA1 || topFull || undefined,
        address_2: topA2 || undefined,
        city: topCity,
        state: topState,
        postal_code: topPc || undefined,
        address_purpose: "LOCATION",
      },
    ];
  }
  return [];
}

/** True if the practice LOCATION city equals the user's city (case-insensitive). */
function validationFacilityAddressContainsCity(facility: any, city: string): boolean {
  const want = city.trim().toUpperCase();
  if (!want) return true;
  for (const a of collectLocationAddresses(facility)) {
    const c = String((a as { city?: string })?.city ?? "")
      .trim()
      .toUpperCase();
    if (c === want) return true;
  }
  return false;
}

/** True if any LOCATION address state matches the 2-letter abbreviation (when provided). */
function validationFacilityAddressMatchesState(facility: any, stateAbbr: string): boolean {
  const want = stateAbbr.trim().toUpperCase();
  if (!want) return true;
  for (const a of collectLocationAddresses(facility)) {
    const s = String((a as { state?: string })?.state ?? "")
      .trim()
      .toUpperCase();
    if (s === want) return true;
  }
  return false;
}

const NAME_QUERY_STOPWORDS = new Set([
  "the",
  "and",
  "of",
  "in",
  "for",
  "at",
  "a",
  "an",
  "llc",
  "inc",
  "pc",
  "pa",
  "pllc",
  "md",
  "do",
]);

function compactAlnum(s: string): string {
  return s.replace(/[^a-z0-9]/gi, "").toLowerCase();
}

function normalizeFacilityNameForMatch(s: string): string {
  if (!s) return "";
  return s
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function significantTokensFromQuery(q: string): string[] {
  const trimmed = q.trim();
  if (!trimmed) return [];
  const norm = normalizeFacilityNameForMatch(trimmed);
  let tokens = norm
    .split(" ")
    .filter((t) => t.length >= 2 && !NAME_QUERY_STOPWORDS.has(t));
  if (tokens.length <= 1 && norm.length >= 4) {
    const spaced = trimmed.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    if (spaced !== trimmed) {
      const alt = normalizeFacilityNameForMatch(spaced)
        .split(" ")
        .filter((t) => t.length >= 2 && !NAME_QUERY_STOPWORDS.has(t));
      if (alt.length > 1) tokens = alt;
    }
  }
  return tokens;
}

/**
 * Text used to decide if the user's search appears in what they see on the card.
 * NPPES ``site_name`` is the practice / location name (e.g. "NORTH CASCADES… CENTER").
 * ``organization_name`` is often the legal entity only (e.g. "EVERGREEN AT BELLINGHAM, L.L.C.")
 * and must not be joined here — that caused SNFs with no "Evergreen" in the visible title to match.
 */
function primaryOrganizationNameBlobForMatch(facility: any): string {
  const site = (facility?.site_name && String(facility.site_name).trim()) || "";
  if (site) return site;
  const org = (facility?.organization_name && String(facility.organization_name).trim()) || "";
  if (org) return org;
  const basic =
    (facility?.basic?.organization_name && String(facility.basic.organization_name).trim()) || "";
  return basic;
}

/** Registry-backed label only — never the user's search string (that caused false-positive matches). */
function primaryOrganizationNameFromLookupResult(result: any): string {
  const a = (result?.site_name && String(result.site_name).trim()) || "";
  const b = (result?.organization_name && String(result.organization_name).trim()) || "";
  const c =
    (result?.basic?.organization_name && String(result.basic.organization_name).trim()) || "";
  return a || b || c;
}

/**
 * True when each significant query token appears as a substring in the normalized user-visible name
 * (so e.g. "center" matches "centers"). Legal organization names are ignored unless ``site_name`` is missing.
 */
function facilityMatchesOrganizationNameQuery(facility: any, query: string): boolean {
  const q = (query || "").trim();
  if (!q) return true;
  const blobRaw = primaryOrganizationNameBlobForMatch(facility);
  if (!blobRaw) return false;
  const qCompact = compactAlnum(q);
  const tokens = significantTokensFromQuery(q);
  if (tokens.length === 0) {
    return qCompact.length >= 2 && compactAlnum(blobRaw).includes(qCompact);
  }
  const normBlob = normalizeFacilityNameForMatch(blobRaw);
  return tokens.every((t) => t.length >= 2 && normBlob.includes(t));
}

/**
 * NPPES enumeration_type: NPI-2 = organization, NPI-1 = individual.
 * When `wantType2` is true, show organization rows; when false, individual. Facility validation UI
 * always passes true (organizations only).
 * Rows with no enumeration_type are included only in the Type 2 view so default signup still lists legacy rows.
 */
function facilityMatchesNpiTypeFilter(f: any, wantType2: boolean): boolean {
  const raw = String(f?.enumeration_type ?? "").trim().toUpperCase();
  if (!raw) return wantType2;
  const is2 = raw.includes("NPI-2") || raw === "2";
  const is1 = raw.includes("NPI-1") || raw === "1";
  if (wantType2) return is2;
  return is1;
}


/** CMS NPPES public search (organization / provider lookup). */
const NPPES_REGISTRY_SEARCH_URL = "https://npiregistry.cms.hhs.gov/search";

type FacilitySearchTab = "name" | "npi";

type FacilityStateOption = {
  id: number;
  abbr: string;
  name: string;
  label: string;
};

/** Province/territory code → English name for Canadian dropdown labels (e.g. BC (British Columbia)). */
const CANADA_SUBNATIONAL_DISPLAY_NAME: Record<string, string> = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

function isCanadaCountryCodeForStates(countryCode: string | undefined): boolean {
  const c = (countryCode || "").toUpperCase();
  return c === "CAN" || c === "CA";
}

const filterStateComboOptions = createFilterOptions<FacilityStateOption>({
  stringify: (option) => `${option.abbr} ${option.name} ${option.label}`,
});

/** Persist last facility search form + selected card site name per user and country (survives browser restarts). */
type FacilityValidationLastForm = {
  state: string;
  city: string;
  facilityName: string;
  selectedFacility: string;
};

function facilityValidationLastFormKey(userId: string, countryCode: string): string {
  return `unicomply:facilityValidationLastForm:${userId}:${countryCode || "US"}`;
}

function readFacilityValidationLastFormForKey(
  userId: string,
  countryCode: string,
): FacilityValidationLastForm | null {
  try {
    const raw = localStorage.getItem(facilityValidationLastFormKey(userId, countryCode));
    if (!raw) return null;
    const o = JSON.parse(raw) as Record<string, unknown>;
    if (typeof o !== "object" || o === null) return null;
    return {
      state: typeof o.state === "string" ? o.state : "",
      city: typeof o.city === "string" ? o.city : "",
      facilityName: typeof o.facilityName === "string" ? o.facilityName : "",
      selectedFacility:
        typeof o.selectedFacility === "string" ? o.selectedFacility : "",
    };
  } catch {
    return null;
  }
}

function formHasLocationData(f: FacilityValidationLastForm | null): boolean {
  if (!f) return false;
  return (
    (f.state?.trim() ?? "") !== "" ||
    (f.city?.trim() ?? "") !== "" ||
    (f.facilityName?.trim() ?? "") !== ""
  );
}

/** Prefer current user key; fall back to guest "0" and USA/US key variants so restores work after login or legacy keys. */
function readFacilityValidationLastFormMerged(
  userId: string,
  countryCode: string,
): FacilityValidationLastForm | null {
  const tryRead = (uid: string, cc: string) =>
    readFacilityValidationLastFormForKey(uid, cc);

  let best = tryRead(userId, countryCode);
  if (formHasLocationData(best)) return best;

  if (userId !== "0") {
    const guest = tryRead("0", countryCode);
    if (formHasLocationData(guest)) return guest;
  }

  const altCc =
    countryCode === "USA" ? "US" : countryCode === "US" ? "USA" : null;
  if (altCc) {
    const alt = tryRead(userId, altCc);
    if (formHasLocationData(alt)) return alt;
    if (userId !== "0") {
      const altGuest = tryRead("0", altCc);
      if (formHasLocationData(altGuest)) return altGuest;
    }
  }

  return best;
}

/** Parse leading abbrev from strings like ``WA (Washington)``. */
function parseAbbrevFromStateLabelInput(raw: string): string {
  const m = (raw || "").trim().match(/^([A-Za-z]{1,8})\s*\(/);
  if (!m) return "";
  return m[1].toUpperCase().slice(0, 8);
}

/**
 * Resolve user input to a single state abbreviation when unambiguous (abbrev, full name, unique
 * substring on state name, or ``WA (Washington)`` style).
 */
function resolveStateAbbrevFromUserInput(raw: string, statesList: StateData[]): string {
  const t = (raw || "").trim();
  if (!t || !statesList.length) return "";
  const up = t.toUpperCase();
  const tl = t.toLowerCase();

  const fromLabel = parseAbbrevFromStateLabelInput(t);
  if (fromLabel) {
    const hit = statesList.find(
      (s) => (s.state_abbreviation || "").toUpperCase() === fromLabel,
    );
    if (hit?.state_abbreviation)
      return String(hit.state_abbreviation).toUpperCase().slice(0, 8);
  }

  const exactAbbr = statesList.find(
    (s) => (s.state_abbreviation || "").toUpperCase() === up,
  );
  if (exactAbbr?.state_abbreviation)
    return String(exactAbbr.state_abbreviation).toUpperCase().slice(0, 8);

  const exactProv = statesList.find(
    (s) => String(s.state_province || "").toUpperCase() === up,
  );
  if (exactProv?.state_abbreviation)
    return String(exactProv.state_abbreviation).toUpperCase().slice(0, 8);

  const exactName = statesList.find((s) => (s.name || "").toLowerCase() === tl);
  if (exactName?.state_abbreviation)
    return String(exactName.state_abbreviation).toUpperCase().slice(0, 8);

  const nameHits = statesList.filter((s) => {
    const n = (s.name || "").toLowerCase();
    return n.includes(tl);
  });
  if (nameHits.length === 1 && nameHits[0].state_abbreviation)
    return String(nameHits[0].state_abbreviation).toUpperCase().slice(0, 8);

  return "";
}

function facilityStateOptionsFromList(
  statesList: StateData[],
  countryCode?: string,
): FacilityStateOption[] {
  const canada = isCanadaCountryCodeForStates(countryCode);
  return statesList
    .map((s) => {
      const abbr = (s.state_abbreviation || "").trim().toUpperCase();
      if (!abbr) return null;
      const rawName = (s.name || "").trim();
      let name = rawName || abbr;
      if (canada && CANADA_SUBNATIONAL_DISPLAY_NAME[abbr]) {
        name = CANADA_SUBNATIONAL_DISPLAY_NAME[abbr];
      } else if (!rawName) {
        name = abbr;
      }
      return {
        id: s.id,
        abbr,
        name,
        label: `${abbr} (${name})`,
      };
    })
    .filter((o): o is FacilityStateOption => o !== null);
}

function findFacilityStateOptionByAbbrev(
  abbrev: string,
  options: FacilityStateOption[],
): FacilityStateOption | null {
  const up = (abbrev || "").trim().toUpperCase();
  if (!up) return null;
  return options.find((o) => o.abbr === up) ?? null;
}

/** Map stored state (full name, abbreviation, or free text) to the text field value (prefer 2-letter abbrev when known). */
function normalizeStoredStateForTextInput(
  raw: string,
  statesList: StateData[],
): string {
  const t = (raw || "").trim();
  if (!t) return "";
  if (statesList.length === 0) {
    const up = t.toUpperCase();
    return up.length <= 8 ? up : up.slice(0, 8);
  }
  const byName = statesList.find((s) => s.name === t);
  if (byName?.state_abbreviation)
    return String(byName.state_abbreviation).toUpperCase().slice(0, 8);
  const up = t.toUpperCase();
  const byAbbrev = statesList.find(
    (s) =>
      (s.state_abbreviation || "").toUpperCase() === up ||
      (s.state_province || "").toUpperCase() === up,
  );
  if (byAbbrev?.state_abbreviation)
    return String(byAbbrev.state_abbreviation).toUpperCase().slice(0, 8);
  const fuzzy = resolveStateAbbrevFromUserInput(t, statesList);
  if (fuzzy) return fuzzy;
  return up.length <= 8 ? up : up.slice(0, 8);
}

/** Resolve typed state to a value suitable for NPPES / API (abbreviation when list matches). */
function resolveStateAbbrevForApi(raw: string, statesList: StateData[]): string {
  const t = (raw || "").trim();
  if (!t) return "";
  if (!statesList.length) return t.toUpperCase().slice(0, 8);
  const tl = t.toLowerCase();
  const matched = statesList.find(
    (s) =>
      (s.name || "").toLowerCase() === tl ||
      String(s.state_province || "").toLowerCase() === tl ||
      (s.state_abbreviation || "").toUpperCase() === t.toUpperCase(),
  );
  if (matched?.state_abbreviation)
    return String(matched.state_abbreviation).toUpperCase().slice(0, 8);
  const fuzzy = resolveStateAbbrevFromUserInput(t, statesList);
  if (fuzzy) return fuzzy;
  return t.toUpperCase().slice(0, 8);
}

function getFacilityValidationLastFormStorageKey(): string {
  return facilityValidationLastFormKey(getCurrentUserId() || "0", getSelectedCountryCode() || "US");
}

function writeFacilityValidationLastFormToLocalStorage(data: FacilityValidationLastForm): void {
  try {
    localStorage.setItem(getFacilityValidationLastFormStorageKey(), JSON.stringify(data));
  } catch {
    // ignore quota / private mode
  }
}

export default function FacilityValidationScreen({
  onValidate,
}: FacilityValidationScreenProps) {
  const location = useLocation();
  const reset = (location.state as any)?.reset === true;
  const fromPaymentFailure =
    (location.state as any)?.fromPaymentFailure === true;
  const [states, setStates] = useState<StateData[]>([]);
  const [selectedState, setSelectedState] = useState<string>("");
  const [City, setCity] = useState<string>("");
  const [facilityName, setFacilityName] = useState<string>("");
  const [npiNumber, setNpiNumber] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  /** Only hydrate phone once per mount/reset so later state reloads do not stomp edits. */
  const phoneHydratedRef = useRef(false);
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<
    "facilities" | "plans" | "adding" | null
  >(null);
  const [statesLoading, setStatesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string>("Validation Error");
  const [npiVerified, setNpiVerified] = useState(false);
  const [npiData, setNpiData] = useState<NPIResult | null>(null);
  const [npiError, setNpiError] = useState<string | null>(null);
  const [npiSuccessMessage, setNpiSuccessMessage] = useState<string | null>(
    null,
  );
  const [selectedCountry, setSelectedCountry] = useState<string>(
    getSelectedCountryName(),
  );
  const [associationDialogOpen, setAssociationDialogOpen] = useState(false);
  const [associationDialogTitle, setAssociationDialogTitle] = useState(
    "Facility already set up",
  );
  const [associationDialogMessage, setAssociationDialogMessage] =
    useState<string>("");
  /** Ignore PW/user re-clicks while catalog resolve + associate are in flight (CI 33177). */
  const associationClickInFlightRef = useRef(false);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [hasExistingFacilities, setHasExistingFacilities] =
    useState<boolean>(false);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);
  const [searchTab, setSearchTab] = useState<FacilitySearchTab>("name");
  const stateOptions = useMemo(
    () => facilityStateOptionsFromList(states, getSelectedCountryCode()),
    [states, selectedCountry],
  );
  const stateOrProvinceLabel = useMemo(() => {
    const code = (getSelectedCountryCode() || "USA").toUpperCase();
    if (code === "CAN" || code === "CA") return "Province";
    if (code === "ARE" || code === "AE" || code === "UAE") return "Emirate";
    return "State";
  }, [selectedCountry]);

  const { stateLocationPlaceholder, cityLocationPlaceholder } = useMemo(() => {
    const code = (getSelectedCountryCode() || "USA").toUpperCase();
    if (code === "CAN" || code === "CA") {
      return {
        stateLocationPlaceholder: "e.g. BC or British Columbia",
        cityLocationPlaceholder: "e.g. Vancouver",
      };
    }
    if (code === "IND" || code === "IN") {
      return {
        stateLocationPlaceholder: "e.g. MH or Maharashtra",
        cityLocationPlaceholder: "e.g. Mumbai",
      };
    }
    if (code === "ARE" || code === "AE" || code === "UAE") {
      return {
        stateLocationPlaceholder: "e.g. DU or Dubai",
        cityLocationPlaceholder: "e.g. Dubai",
      };
    }
    return {
      stateLocationPlaceholder: "e.g. WA or Washington",
      cityLocationPlaceholder: "e.g. Sacramento",
    };
  }, [selectedCountry]);

  const facilityMatchFootnoteLines = useMemo(() => {
    const code = (getSelectedCountryCode() || "USA").toUpperCase();
    const usa = code === "USA" || code === "US";
    if (usa) {
      return {
        facilities:
          "Adjust state, city, or facility name to match registry addresses. Only organization (NPI Type 2) facilities are shown.",
        suggestions:
          "Adjust state, city, or facility name. Only organization (NPI Type 2) facilities are shown.",
        type2FilteredLine:
          "None of the results that match your filters are NPI Type 2 (organization) records.",
      };
    }
    return {
      facilities:
        "Adjust province or territory, city, or facility name so it matches a suggested site.",
      suggestions:
        "Adjust province or territory, city, or facility name so it matches a suggested site.",
      type2FilteredLine:
        "None of the suggested facilities that pass your filters are shown. Try broadening the facility name or location.",
    };
  }, [selectedCountry]);

  const [stateComboInput, setStateComboInput] = useState("");
  const stateFieldFocusedRef = useRef(false);

  useEffect(() => {
    if (statesLoading || stateFieldFocusedRef.current) return;
    const opt = findFacilityStateOptionByAbbrev(selectedState, stateOptions);
    setStateComboInput(opt?.label ?? "");
  }, [selectedState, stateOptions, statesLoading]);

  // Active MCM trial → single facility + verification.
  // No active trial → multi cart (cap) unlocked.
  const [existingActiveFacilityCount, setExistingActiveFacilityCount] =
    useState(0);
  const [existingUserFacilities, setExistingUserFacilities] = useState<
    UserFacility[]
  >([]);
  const layoutData = useFacilityLayoutDataOptional();
  // Prefer layout list (same as Active Facilities); else getUserFacilities fallback.
  const ownedFacilityKeys = useMemo(() => {
    const list =
      (layoutData?.userFacilities?.length
        ? layoutData.userFacilities
        : existingUserFacilities
      ).filter((f) => !f.is_network);
    return collectActiveFacilitySidebarDedupeKeys(list);
  }, [layoutData?.userFacilities, existingUserFacilities]);
  /** Rules /controls Features — Add by type on empty search. Default on. */
  const [addFacilityByTypeEnabled, setAddFacilityByTypeEnabled] =
    useState(true);
  const [multiFacilityUnlocked, setMultiFacilityUnlocked] = useState(() => {
    try {
      const raw = sessionStorage.getItem(TRIAL_FACILITY_GATE_SESSION_KEY);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as TrialFacilityGateResult;
      return parsed.multi_facility_unlocked === true;
    } catch {
      return false;
    }
  });
  const [facilityCart, setFacilityCart] = useState<CartFacilityItem[]>(() =>
    readFacilityCart(),
  );
  const multiSelectEnabled = multiFacilityUnlocked || userIsSalesUser();
  const cartCap = effectiveSelfServeCartCap(multiSelectEnabled);

  const removeCartFacility = (cartKey: string) => {
    const item = facilityCart.find((f) => f.cart_key === cartKey);
    const next = removeFacilityFromCart(cartKey);
    setFacilityCart(next);
    const upfId = Number(item?.id);
    if (Number.isFinite(upfId) && upfId > 0) {
      void softDeleteUserPreferredFacility(upfId).catch(() => {});
    }
  };

  useEffect(() => {
    const currentUserId = Number(getCurrentUserId() || "0");
    if (!currentUserId) return;

    // Separate fetches — gate failure must not block getUserFacilities.
    getUserFacilities(currentUserId)
      .then((facRes) => {
        const facilities = (facRes.facilities ?? []).filter((f) => !f.is_network);
        setExistingUserFacilities(facilities);
        setExistingActiveFacilityCount(facilities.length);
        if (facilities.length > 0) setHasExistingFacilities(true);
      })
      .catch(() => {});

    billingService
      .getTrialFacilityGate(currentUserId)
      .then((gate) => {
        setMultiFacilityUnlocked(gate.multi_facility_unlocked === true);
      })
      .catch(() => {
        // Fail closed: keep multi locked until trial check succeeds.
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAddFacilityByTypeSettings().then((s) => {
      if (!cancelled) {
        setAddFacilityByTypeEnabled(s.enabled);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // If invoked with reset flag, clear any persisted/session data and wipe local state
  // But don't clear if coming from payment failure (we want to preserve values)
  useEffect(() => {
    if (!reset || fromPaymentFailure) return;
    try {
      sessionStorage.removeItem("filtered_selectedFacility");
      sessionStorage.removeItem("filtered_facilityTypes");
      sessionStorage.removeItem("filtered_category");
      sessionStorage.removeItem("DisplayPage");
      sessionStorage.removeItem("sessionState");
      sessionStorage.removeItem("sessionCity");
      sessionStorage.removeItem("sessionFacilityName");
      sessionStorage.removeItem("sessionNpiNumber");
      sessionStorage.removeItem("sessionPhoneNumber");
      localStorage.removeItem(getFacilityValidationLastFormStorageKey());
    } catch (_) {}

    setSelectedState("");
    setCity("");
    setFacilityName("");
    setNpiNumber("");
    setPhoneNumber("");
    phoneHydratedRef.current = false;
    // keep email auto-filled for logged-in user
    setValidationResult(null);
    setSelectedFacility("");
    setIsValidated(false);
    setNpiVerified(false);
    setNpiData(null);
    setNpiError(null);
    setError(null);
    setErrorTitle("Validation Error");
    setSearchTab("name");
  }, [reset, fromPaymentFailure]);

  // Validation result state
  const [validationResult, setValidationResult] =
    useState<FacilityValidationResult | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<string>("");
  /** Organization (NPI-2) only — no user toggle; aligns with NPPES org search. */
  const npiType2Only = true;
  const [isValidated, setIsValidated] = useState(false);
  const [validationFromNpi, setValidationFromNpi] = useState(false);
  const [providerTypeOptions, setProviderTypeOptions] = useState<
    ProviderTypeOption[]
  >([]);
  const [providerTypeOptionsLoading, setProviderTypeOptionsLoading] =
    useState(false);

  /** Avoid writing localStorage until after restore effect has applied (same tick would clobber saved prefs). */
  const facilityFormPersistReadyRef = useRef(false);
  /** Only clear location fields when country changes; remount with saved localStorage must not wipe them. */
  const prevFacilityValidationCountryRef = useRef<string | null>(null);

  const navigate = useNavigate();

  // Bridge: allow the legacy facility-context.js script to push its
  // validation results (including NPI-based lookups) into this React
  // component so we can reuse the same facility cards UI.
  useEffect(() => {
    (window as any).displayValidationResults = (
      validationResponse: any,
    ): void => {
      try {
        if (!validationResponse) return;

        // Normalize into FacilityValidationResult shape used by React
        const normalized: FacilityValidationResult = {
          success: true,
          is_valid: validationResponse.is_valid,
          facilities: validationResponse.facility
            ? [validationResponse.facility]
            : validationResponse.facilities || [],
          suggestions: validationResponse.suggestions || [],
          message: validationResponse.message,
        };

        setValidationResult(normalized);
        setIsValidated(true);
        setError(null);
        setErrorTitle("Validation Error");

        // Mark that this validation came from NPI (legacy JS) if flagged
        const fromNpi = !!validationResponse.npi_verified;
        setValidationFromNpi(fromNpi);

        // Capture NPI success message if present
        if (fromNpi && validationResponse.message) {
          setNpiSuccessMessage(validationResponse.message);
        }
      } catch (e) {
        console.error("Failed to apply legacy validation results:", e);
      }
    };

    return () => {
      // Clean up to avoid leaking handlers across mounts
      if ((window as any).displayValidationResults) {
        delete (window as any).displayValidationResults;
      }
    };
  }, []);

  // Load provider type options when selected facility type is not confirmed and no type already determined (ambiguous NPI)
  useEffect(() => {
    const selected = getSelectedFacility(validationResult ?? null);
    const hasDeterminedType = !!(
      ((selected as any)?.facility_type_slug ?? "").trim() ||
      ((selected as any)?.facility_type ?? "").toString().trim()
    );
    const needOptions =
      selected &&
      selected.facility_type_confirmed === false &&
      !hasDeterminedType;
    if (!needOptions) {
      setProviderTypeOptions([]);
      return;
    }
    const countryCode =
      (selected as any)?.country_code ||
      (selected as any)?.country ||
      getSelectedCountryCode();
    const useCertifiedList = (selected as any)?.facility_type_requires_selection === true;
    let cancelled = false;
    setProviderTypeOptionsLoading(true);
    (useCertifiedList ? getCertifiedProviderTypeOptions(countryCode) : getProviderTypeOptions(countryCode))
      .then((res) => {
        if (!cancelled) setProviderTypeOptions(res.provider_type_options ?? []);
      })
      .catch(() => {
        if (!cancelled) setProviderTypeOptions([]);
      })
      .finally(() => {
        if (!cancelled) setProviderTypeOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [validationResult]);

  /** NPPES addresses use 2-letter state; field stores typed abbrev (or resolvable name). */
  const selectedStateForAddressFilter = useMemo(
    () => resolveStateAbbrevForApi(selectedState, states),
    [selectedState, states],
  );

  const regionFilteredValidationFacilities = useMemo(() => {
    const list = validationResult?.facilities;
    if (!Array.isArray(list)) return [];
    const c = (City || "").trim();
    const st = selectedStateForAddressFilter;
    const fn = facilityName.trim();
    const skipClientFilters = validationFromNpi;
    return list.filter((f: any) => {
      if (skipClientFilters) return true;
      if (st && !validationFacilityAddressMatchesState(f, st)) return false;
      if (c && !validationFacilityAddressContainsCity(f, c)) return false;
      if (fn && !facilityMatchesOrganizationNameQuery(f, fn)) return false;
      return true;
    });
  }, [
    validationResult?.facilities,
    City,
    facilityName,
    selectedStateForAddressFilter,
    validationFromNpi,
  ]);

  const displayedValidationFacilities = useMemo(() => {
    return regionFilteredValidationFacilities.filter((f: any) =>
      facilityMatchesNpiTypeFilter(f, npiType2Only),
    );
  }, [regionFilteredValidationFacilities, npiType2Only]);

  const regionFilteredSuggestionFacilities = useMemo(() => {
    const list = validationResult?.suggestions;
    if (!Array.isArray(list)) return [];
    const c = (City || "").trim();
    const st = selectedStateForAddressFilter;
    const fn = facilityName.trim();
    const skipClientFilters = validationFromNpi;
    return list.filter((f: any) => {
      if (skipClientFilters) return true;
      if (st && !validationFacilityAddressMatchesState(f, st)) return false;
      if (c && !validationFacilityAddressContainsCity(f, c)) return false;
      if (fn && !facilityMatchesOrganizationNameQuery(f, fn)) return false;
      return true;
    });
  }, [
    validationResult?.suggestions,
    City,
    facilityName,
    selectedStateForAddressFilter,
    validationFromNpi,
  ]);

  const displayedSuggestionFacilities = useMemo(() => {
    return regionFilteredSuggestionFacilities.filter((f: any) =>
      facilityMatchesNpiTypeFilter(f, npiType2Only),
    );
  }, [regionFilteredSuggestionFacilities, npiType2Only]);

  const showAddFacilityByType = shouldShowAddFacilityByTypeEmptyState({
    enabled: addFacilityByTypeEnabled,
    isValidated,
    errorTitle,
    error,
    npiError,
    displayedCount: displayedValidationFacilities.length,
    rawType2CandidateCount: regionFilteredValidationFacilities.length,
    suggestionDisplayedCount: displayedSuggestionFacilities.length,
    suggestionRawCount: regionFilteredSuggestionFacilities.length,
  });

  useEffect(() => {
    if (!isValidated || !validationResult?.success) return;

    if (validationResult.is_valid === true) {
      const rawLen = validationResult.facilities?.length ?? 0;
      if (rawLen > 0 && displayedValidationFacilities.length === 0) {
        setSelectedFacility("");
        setValidationResult((prev: any) => {
          if (!prev?.facilities) return prev;
          return {
            ...prev,
            facilities: prev.facilities.map((f: any) => ({ ...f, selected: false })),
          };
        });
        return;
      }
      if (!selectedFacility) return;
      const still = displayedValidationFacilities.some(
        (f: any) => f.site_name === selectedFacility,
      );
      if (!still) {
        setSelectedFacility("");
        setValidationResult((prev: any) => {
          if (!prev?.facilities) return prev;
          return {
            ...prev,
            facilities: prev.facilities.map((f: any) => ({ ...f, selected: false })),
          };
        });
      }
      return;
    }

    if (validationResult.is_valid === false) {
      const rawLen = validationResult.suggestions?.length ?? 0;
      if (rawLen > 0 && displayedSuggestionFacilities.length === 0) {
        setSelectedFacility("");
        setValidationResult((prev: any) => {
          if (!prev?.suggestions) return prev;
          return {
            ...prev,
            suggestions: prev.suggestions.map((s: any) => ({ ...s, selected: false })),
          };
        });
        return;
      }
      if (!selectedFacility) return;
      const still = displayedSuggestionFacilities.some(
        (f: any) => f.site_name === selectedFacility,
      );
      if (!still) {
        setSelectedFacility("");
        setValidationResult((prev: any) => {
          if (!prev?.suggestions) return prev;
          return {
            ...prev,
            suggestions: prev.suggestions.map((s: any) => ({ ...s, selected: false })),
          };
        });
      }
    }
  }, [
    isValidated,
    validationResult?.success,
    validationResult?.is_valid,
    validationResult?.facilities,
    validationResult?.suggestions,
    displayedValidationFacilities,
    displayedSuggestionFacilities,
    selectedFacility,
  ]);

  // Listen for country changes from localStorage
  useEffect(() => {
    const checkCountryChange = () => {
      const currentCountry = getSelectedCountryName();
      if (currentCountry !== selectedCountry) {
        setSelectedCountry(currentCountry);
      }
    };

    // Check immediately
    checkCountryChange();

    // Set up interval to check for changes
    const intervalId = setInterval(checkCountryChange, 500);

    // Also listen to storage events (for changes from other tabs)
    window.addEventListener("storage", checkCountryChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", checkCountryChange);
    };
  }, [selectedCountry]);

  // Load states when component mounts or country changes
  useEffect(() => {
    const loadStates = async () => {
      try {
        setStatesLoading(true);
        setError(null);
        setErrorTitle("Validation Error");
        const statesData = await getStates(selectedCountry);
        setStates(statesData);

        // Only reset form fields when country actually changes, not on mount/remount.
        // The previous "isInitialLoad" check treated any saved local/session data as
        // "not initial" and cleared fields on every navigation/refresh.
        const prevCountry = prevFacilityValidationCountryRef.current;
        const countryChanged =
          prevCountry !== null && prevCountry !== selectedCountry;
        prevFacilityValidationCountryRef.current = selectedCountry;

        if (countryChanged && !fromPaymentFailure) {
          try {
            sessionStorage.removeItem("sessionState");
            sessionStorage.removeItem("sessionCity");
            sessionStorage.removeItem("sessionFacilityName");
            sessionStorage.removeItem("filtered_selectedFacility");
            sessionStorage.removeItem("filtered_facilityTypes");
            sessionStorage.removeItem("filtered_category");
            sessionStorage.removeItem("preferred_facility_for_pricing");
            sessionStorage.removeItem("DisplayPage");
            sessionStorage.removeItem("sessionNpiNumber");
            sessionStorage.removeItem("sessionPhoneNumber");
            localStorage.removeItem("selectedFacility");
          } catch {
            /* ignore */
          }
          setSelectedState("");
          setCity("");
          setFacilityName("");
          setValidationResult(null);
          setSelectedFacility("");
          setIsValidated(false);
        }
      } catch (err) {
        console.error("Failed to load states:", err);
        setError("Failed to load states");
      } finally {
        setStatesLoading(false);
      }
    };

    loadStates();
  }, [selectedCountry, fromPaymentFailure]);

  const handleFacilityNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFacilityName(event.target.value);
  };
  const handleCityNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCity(event.target.value);
  };
  const handleNpiNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 10);
    setNpiNumber(digits);
    setNpiVerified(false); // Reset verification when NPI changes
    setNpiData(null); // Clear previous NPI data
    setNpiError(null); // Clear previous errors
    setNpiSuccessMessage(null); // Clear previous success message
  };

  const handleSearchTabChange = (tab: FacilitySearchTab) => {
    if (tab === searchTab) return;
    setSearchTab(tab);
    setError(null);
    setNpiError(null);
    setNpiSuccessMessage(null);
    setIsValidated(false);
    setValidationResult(null);
    setSelectedFacility("");
    setNpiData(null);
    setNpiVerified(false);
    setValidationFromNpi(false);
  };
  const handlePhoneNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPhoneNumber(event.target.value);
  };
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  // Restore session data after states are loaded
  // Always restore when coming from payment failure, otherwise only if not reset
  useEffect(() => {
    if (statesLoading) return;

    // Don't restore if reset flag is true and not coming from payment failure
    if (reset && !fromPaymentFailure) return;

    const storedState = sessionStorage.getItem("sessionState");
    const storedCity = sessionStorage.getItem("sessionCity");
    const storedFacilityName = sessionStorage.getItem("sessionFacilityName");
    const storedNpiNumber = sessionStorage.getItem("sessionNpiNumber");
    const storedPhoneNumber = sessionStorage.getItem("sessionPhoneNumber");
    const localForm = readFacilityValidationLastFormMerged(
      getCurrentUserId() || "0",
      getSelectedCountryCode() || "US",
    );
    // const storedEmail = sessionStorage.getItem("sessionEmail");

    const mergedState = storedState || localForm?.state || "";
    const mergedCity = storedCity || localForm?.city || "";
    const mergedFacilityName = storedFacilityName || localForm?.facilityName || "";
    const normalizedState = normalizeStoredStateForTextInput(mergedState, states);

    setSelectedState(normalizedState);
    setCity(mergedCity);
    setFacilityName(mergedFacilityName);
    try {
      sessionStorage.setItem("sessionState", normalizedState);
      sessionStorage.setItem("sessionCity", mergedCity);
      sessionStorage.setItem("sessionFacilityName", mergedFacilityName);
    } catch {
      // ignore quota / private mode
    }
    if (storedNpiNumber) {
      setNpiNumber(storedNpiNumber);
    }
    // Prefer saved profile phone over session scratch value so refresh
    // always reflects the user record (same source as Profile page).
    if (!phoneHydratedRef.current) {
      const profilePhone = getCurrentUserPhone();
      const mergedPhone = profilePhone || storedPhoneNumber || "";
      if (mergedPhone) {
        setPhoneNumber(mergedPhone);
        try {
          sessionStorage.setItem("sessionPhoneNumber", mergedPhone);
        } catch {
          // ignore quota / private mode
        }
      }
      phoneHydratedRef.current = true;
    }
    if (!sessionStorage.getItem("filtered_selectedFacility") && localForm?.selectedFacility) {
      setSelectedFacility(localForm.selectedFacility);
    }
    // if (storedEmail) {
    //   setEmail(storedEmail);
    // }
  }, [statesLoading, states, reset, fromPaymentFailure, selectedCountry]);

  useEffect(() => {
    if (statesLoading) {
      facilityFormPersistReadyRef.current = false;
    }
  }, [statesLoading]);

  // Persist last state / city / facility name / selected facility for this user + country (localStorage).
  useEffect(() => {
    if (statesLoading) return;
    if (reset && !fromPaymentFailure) return;

    if (!facilityFormPersistReadyRef.current) {
      facilityFormPersistReadyRef.current = true;
      return;
    }

    writeFacilityValidationLastFormToLocalStorage({
      state: selectedState,
      city: City,
      facilityName,
      selectedFacility,
    });
    try {
      sessionStorage.setItem("sessionState", selectedState);
      sessionStorage.setItem("sessionCity", City);
      sessionStorage.setItem("sessionFacilityName", facilityName);
    } catch {
      // ignore quota / private mode
    }
  }, [
    selectedState,
    City,
    facilityName,
    selectedFacility,
    statesLoading,
    reset,
    fromPaymentFailure,
    selectedCountry,
  ]);

  // Prefill email with logged-in user's email (always apply, even after reset)
  useEffect(() => {
    const user = getCurrentUser();
    const fallback = sessionStorage.getItem("UserEmail");
    if (user?.email) {
      setEmail(user.email);
    } else if (fallback) {
      setEmail(fallback);
    }
  }, [reset]);

  // Prefill phone with logged-in user's profile phone (like email).
  // Runs on mount/reset; restore effect also hydrates once after states load.
  useEffect(() => {
    const profilePhone = getCurrentUserPhone();
    if (!profilePhone) return;
    setPhoneNumber(profilePhone);
    phoneHydratedRef.current = true;
    try {
      sessionStorage.setItem("sessionPhoneNumber", profilePhone);
    } catch {
      // ignore quota / private mode
    }
  }, [reset]);

  // Helper function to set error with custom title
  const setValidationError = (
    message: string,
    title: string = "Validation Error",
  ) => {
    setError(message);
    setErrorTitle(title);
  };

  const handleValidate = async () => {
    // Clear any previous NPI validation messages when starting a new validation
    const legacyResultsDiv =
      typeof document !== "undefined"
        ? document.getElementById("validationResults")
        : null;
    if (legacyResultsDiv) {
      legacyResultsDiv.style.display = "none";
      legacyResultsDiv.innerHTML = "";
    }

    const effectivePhoneNumber = phoneNumber.trim() || "0123456789";
    const stateToSend = resolveStateAbbrevForApi(selectedState, states);
    const countryIso = (getSelectedCountryCode() || "USA").trim().toUpperCase();
    const useUsaNpiRegistry = isUnitedStatesCountryCode(countryIso);

    if (!useUsaNpiRegistry && searchTab === "npi") {
      setValidationError(
        "US NPI numbers apply only to United States facilities. Use “Search by name” with your facility name, city, and province, or switch the workspace country to United States.",
        "NPI lookup (US only)",
      );
      setErrorTitle("NPI lookup (US only)");
      return;
    }

    if (!useUsaNpiRegistry && searchTab === "name" && !City.trim()) {
      setValidationError(
        "City is required to search for facilities outside the United States.",
        "Validation Error",
      );
      setErrorTitle("Validation Error");
      return;
    }

    if (searchTab === "name") {
      if (!facilityName.trim() || !phoneNumber.trim() || !email.trim()) {
        setValidationError(
          "Please fill in Facility Name, Phone Number, and Email.",
        );
        setErrorTitle("Validation Error");
        return;
      }
      if (!stateToSend) {
        setValidationError(
          "Please enter state (abbreviation or pick from suggestions).",
        );
        setErrorTitle("Validation Error");
        return;
      }
    } else {
      if (!/^\d{10}$/.test(npiNumber)) {
        setValidationError(
          "Enter a valid 10-digit NPI. Only Type 2 (organizational) NPIs are supported.",
        );
        setErrorTitle("Validation Error");
        return;
      }
      if (!phoneNumber.trim() || !email.trim()) {
        setValidationError("Please fill in Phone Number and Email.");
        setErrorTitle("Validation Error");
        return;
      }
    }

    setLoading(true);
    setLoadingMessage("facilities");
    setError(null);
    setNpiError(null);
    sessionStorage.setItem("facilitySearchInProgress", "1");

    try {
      if (searchTab === "name") {
        const trimmedName = facilityName.trim();
        setNpiData(null);
        setNpiVerified(false);
        setValidationFromNpi(false);

        if (!useUsaNpiRegistry) {
          const apiCountry = countryIso === "CA" ? "CAN" : countryIso;
          const fv = await validateFacility({
            site_name: trimmedName,
            city: City.trim(),
            state: stateToSend!,
            country: apiCountry,
          });

          if (!fv.success) {
            setError(fv.error || "Facility search failed. Please try again.");
            setValidationResult(null);
            setIsValidated(false);
            return;
          }

          const toDisplayRows = (rows: any[]) =>
            rows.map((row: any) => ({
              site_name: row.site_name,
              city: row.city,
              state: row.state || stateToSend,
              address_1: row.address_1,
              address_2: row.address_2,
              postal_code: row.postal_code,
              full_address: row.full_address,
              country: row.country,
              country_code: row.country_code ?? row.country,
              facility_url: row.facility_url,
              facility_type: row.facility_type ?? "",
              facility_type_id: row.facility_type_id,
              facility_type_slug: row.facility_type_slug,
              category: row.category,
              npi_number: row.npi_number || "",
              enumeration_type: "NPI-2",
              organization_name: row.organization_name || row.site_name,
              primary_address: primaryAddressFromFacilityValidationRow(
                row as Record<string, unknown>,
                stateToSend ?? undefined,
              ),
              already_registered: isFacilityAlreadyRegistered(row),
            }));

          const pickNameMatches = (rows: any[]) => {
            const nameMatch = (f: any) =>
              facilityMatchesOrganizationNameQuery(f, trimmedName);
            return rows.some(nameMatch) ? rows.filter(nameMatch) : rows;
          };

          if (fv.is_valid && (fv.facilities?.length ?? 0) > 0) {
            const mappedFacilities = toDisplayRows(fv.facilities ?? []);
            const filtered = pickNameMatches(mappedFacilities);
            if (filtered.length === 0) {
              setErrorTitle("No matching facilities");
              setError(
                `No results include "${trimmedName}" in the organization name. Try a more specific name.`,
              );
              setValidationResult(null);
              setIsValidated(false);
              return;
            }
            const validationResult: FacilityValidationResult = {
              success: true,
              is_valid: true,
              facilities: filtered,
              suggestions: [],
              message: fv.message || "Facilities found",
            };
            setValidationResult(validationResult);
            setValidationFromNpi(false);
            setSelectedFacility("");
            setIsValidated(true);
            sessionStorage.setItem("sessionState", selectedState);
            sessionStorage.setItem("sessionCity", City);
            sessionStorage.setItem("sessionFacilityName", facilityName);
            sessionStorage.setItem(
              "sessionNpiNumber",
              String(filtered[0]?.npi_number || "").trim() || "",
            );
            sessionStorage.setItem("sessionPhoneNumber", effectivePhoneNumber);
            sessionStorage.setItem("DisplayPage", "true");
            return;
          }

          if (!fv.is_valid && (fv.suggestions?.length ?? 0) > 0) {
            const mappedFacilities = toDisplayRows(fv.suggestions ?? []);
            const filtered = pickNameMatches(mappedFacilities);
            if (filtered.length === 0) {
              setErrorTitle("No matching facilities");
              setError(
                `No suggested facilities include "${trimmedName}" in the name. Try adjusting your search.`,
              );
              setValidationResult(null);
              setIsValidated(false);
              return;
            }
            const validationResult: FacilityValidationResult = {
              success: true,
              is_valid: true,
              facilities: filtered,
              suggestions: [],
              message: fv.message || "Select a facility below.",
            };
            setValidationResult(validationResult);
            setValidationFromNpi(false);
            setSelectedFacility("");
            setIsValidated(true);
            sessionStorage.setItem("sessionState", selectedState);
            sessionStorage.setItem("sessionCity", City);
            sessionStorage.setItem("sessionFacilityName", facilityName);
            sessionStorage.setItem("sessionNpiNumber", "");
            sessionStorage.setItem("sessionPhoneNumber", effectivePhoneNumber);
            sessionStorage.setItem("DisplayPage", "true");
            return;
          }

          setError(
            "No facilities found. Check the facility name, city, and province.",
          );
          setValidationResult(null);
          setIsValidated(false);
          return;
        }

        // United States: organization name via CMS NPPES (optional city/state narrows NPPES).
        const orgLookupResult = await npiLookupService.lookupOrganization({
          organization_name: trimmedName,
          ...(City.trim() ? { city: City.trim() } : {}),
          ...(stateToSend ? { state: stateToSend } : {}),
          country_code: getSelectedCountryCode(),
        });

        if (
          orgLookupResult.success &&
          (orgLookupResult.data?.results?.length ?? 0) > 0
        ) {
          // Map organization lookup results to facility format
          const results = orgLookupResult.data?.results ?? [];
          // Prefetch RHC catalog id once so Mackay cards have facility_type_id before click
          // (CI 33177: click-time await + PW re-clicks never left CMS card for ~30s).
          let rhcCatalogId: number | undefined;
          try {
            const catalog = await getFacilityTypes();
            rhcCatalogId = pickRhcFacilityTypeIdFromCatalog(
              catalog.facility_types || [],
            );
          } catch (e) {
            console.error("prefetch RHC facility_type_id from catalog failed:", e);
          }
          const allMappedFacilities = results.map(
            (result: any) => {
              const snfType = applySnfLookupTypeOverride({
                facility_type:
                  result.facility_type?.display_name ||
                  result.primary_taxonomy?.description ||
                  result.taxonomies?.[0]?.desc ||
                  "",
                facility_type_id: result.facility_type?.id,
                facility_type_slug: result.facility_type?.type_slug,
                primary_taxonomy: result.primary_taxonomy,
                site_name: primaryOrganizationNameFromLookupResult(result),
                organization_name: result.organization_name,
              });
            const rhcType = applyRhcLookupTypeOverride({
              facility_type: snfType.facility_type,
              facility_type_id: snfType.facility_type_id,
              facility_type_slug: snfType.facility_type_slug,
              primary_taxonomy: result.primary_taxonomy,
              ccn: result.ccn,
              ccn_number: result.ccn_number ?? result.ccn,
              match_method: result.facility_type?.match_method,
              facility_type_obj: result.facility_type,
              site_name: result.site_name,
              organization_name: result.organization_name,
            });
            const typedFacility = withRhcCatalogTypeId(
              rhcType.facility_type_slug === "rural_health_clinic" ? rhcType : snfType,
              rhcCatalogId,
            );
              return {
              site_name:
                primaryOrganizationNameFromLookupResult(result) ||
                (result.npi_number
                  ? `Organization (NPI ${String(result.npi_number).trim()})`
                  : "Unnamed organization"),
              city:
                result.primary_address?.city ||
                result.addresses?.[0]?.city ||
                City,
              state:
                result.primary_address?.state ||
                result.addresses?.[0]?.state ||
                stateToSend,
              facility_type: typedFacility.facility_type,
              facility_type_id: typedFacility.facility_type_id,
              facility_type_slug: typedFacility.facility_type_slug,
              category: result.facility_type?.category,
              npi_number: result.npi_number,
              enumeration_type: result.enumeration_type,
              organization_name:
                (result.organization_name && String(result.organization_name).trim()) ||
                (result.basic?.organization_name && String(result.basic.organization_name).trim()) ||
                primaryOrganizationNameFromLookupResult(result) ||
                undefined,
              primary_address: result.primary_address,
              primary_taxonomy: result.primary_taxonomy,
              addresses: result.addresses,
              taxonomies: result.taxonomies,
              other_names: result.other_names,
              taxonomy_match: result.taxonomy_match,
              facility_type_obj: mergeLookupFacilityTypeObj(
                typedFacility,
                result.facility_type || undefined,
              ),
              facility_type_confirmed:
                Boolean(typedFacility.facility_type_slug) ||
                result.facility_type_confirmed === true,
              ccn_number: result.ccn_number ?? result.ccn ?? undefined,
              ccn: result.ccn,
              alternativeNpis: [
                ...(result.alternative_npi_numbers ?? result.alternativeNpis ?? []),
              ],
              // *** ALREADY-REGISTERED GREYING — must be copied here ***
              // Set by backend _apply_claimed_facility_filter (npi_route.py). If dropped here
              // the card will NOT grey out even though the log says "Marked N already-registered".
              already_registered: isFacilityAlreadyRegistered(result),
            };
            },
          );

          // Drop rows where the registry name does not reflect the user's facility name
          // (API can return broad state matches; never use the typed query as a name fallback).
          const mappedFacilities = allMappedFacilities.filter((f: any) =>
            facilityMatchesOrganizationNameQuery(f, trimmedName),
          );

          if (mappedFacilities.length === 0) {
            setErrorTitle("No matching facilities");
            setError(
              `No results include "${trimmedName}" in the organization name. Try a more specific name or add city to narrow the search.`,
            );
            setValidationResult(null);
            setIsValidated(false);
            return;
          }

          // Get the actual NPI number from the first result (use first facility's NPI)
          const actualNpiNumber = mappedFacilities[0]?.npi_number || "";

          // Create validation result with mapped facilities
          const validationResult: FacilityValidationResult = {
            success: true,
            is_valid: true,
            facilities: mappedFacilities,
            suggestions: [],
            message: orgLookupResult.message || "Facilities found",
          };

          setValidationResult(validationResult);
          setValidationFromNpi(false);
          setSelectedFacility(""); // No default selection
          setIsValidated(true);

          // Store session data
          sessionStorage.setItem("sessionState", selectedState);
          sessionStorage.setItem("sessionCity", City);
          sessionStorage.setItem("sessionFacilityName", facilityName);
          // Store actual NPI from API in session storage for use in API calls
          sessionStorage.setItem("sessionNpiNumber", actualNpiNumber);
          sessionStorage.setItem("sessionPhoneNumber", effectivePhoneNumber);
          sessionStorage.setItem("DisplayPage", "true");
        } else if (
          (orgLookupResult.data?.llm_suggestions?.length ?? 0) > 0 &&
          (orgLookupResult.data?.results?.length ?? 0) === 0
        ) {
          // No NPI match but API returned LLM suggestions - show them so user can pick one
          const llmRows = orgLookupResult.data?.llm_suggestions ?? [];
          const mappedFacilitiesRaw = llmRows.map(
            (s: any) => {
              const flatA1 = String(s.address_1 ?? "").trim();
              const flatFull = String(s.full_address ?? "").trim();
              const primaryAddress =
                s.primary_address ??
                (flatA1 || flatFull || String(s.postal_code ?? "").trim()
                  ? {
                      address_1: flatA1 || flatFull || undefined,
                      address_2: s.address_2 ?? undefined,
                      city: s.city,
                      state: s.state,
                      postal_code: s.postal_code ?? undefined,
                      country_code: s.country_code ?? s.country,
                    }
                  : undefined);
              return {
                site_name: s.site_name,
                city: s.city,
                state: s.state,
                address_1: s.address_1,
                address_2: s.address_2,
                postal_code: s.postal_code,
                full_address: s.full_address,
                country: s.country,
                country_code: s.country_code ?? s.country,
                facility_type: s.facility_type || "",
                facility_type_id: s.facility_type_id,
                category: s.category,
                facility_url: s.facility_url,
                similarity_score: s.similarity_score,
                npi_number: s.npi_number ?? "",
                enumeration_type: s.enumeration_type,
                organization_name: s.organization_name,
                primary_address: primaryAddress,
                primary_taxonomy: s.primary_taxonomy,
                addresses: s.addresses,
                ccn_number: s.ccn_number ?? s.ccn ?? undefined,
                ccn: s.ccn,
                already_registered: isFacilityAlreadyRegistered(s),
              };
            },
          );
          const mappedFacilities = mappedFacilitiesRaw.filter((f: any) =>
            facilityMatchesOrganizationNameQuery(f, facilityName.trim()),
          );
          if (mappedFacilities.length === 0) {
            setErrorTitle("No matching facilities");
            setError(
              `No suggested facilities include "${facilityName.trim()}" in the name. Try adjusting your search.`,
            );
            setValidationResult(null);
            setIsValidated(false);
            return;
          }
          const validationResult: FacilityValidationResult = {
            success: true,
            is_valid: true,
            facilities: mappedFacilities,
            suggestions: [],
            message:
              orgLookupResult.message ||
              "No exact NPI match. Select a facility below or add by address.",
          };
          setValidationResult(validationResult);
          setValidationFromNpi(false);
          setSelectedFacility("");
          setIsValidated(true);
          sessionStorage.setItem("sessionState", selectedState);
          sessionStorage.setItem("sessionCity", City);
          sessionStorage.setItem("sessionFacilityName", facilityName);
          sessionStorage.setItem("sessionNpiNumber", "");
          sessionStorage.setItem("sessionPhoneNumber", effectivePhoneNumber);
          sessionStorage.setItem("DisplayPage", "true");
        } else {
          // No results found - check for specific error codes
          let errorCode = "";
          if (
            orgLookupResult.error &&
            typeof orgLookupResult.error === "object"
          ) {
            errorCode = (orgLookupResult.error as any).code || "";
          }

          if (errorCode === "NO_FACILITIES_FOUND") {
            // Physician/provider NPIs were found but no healthcare facilities
            setErrorTitle("Provider Type Not Supported");
            setError(
              "UniComply provides compliance analysis for healthcare facilities, not individual physicians or provider practices. " +
                "The search results returned physician specialties (e.g., OB/GYN, Family Medicine, Nurse Practitioners) which are not supported. " +
                "Please search for a hospital, clinic, nursing facility, pharmacy, or other healthcare facility instead.",
            );
          } else {
            // Generic no results error
            setError(
              "No facilities found. Check the facility name, or add optional city and state to narrow the search.",
            );
          }
          setValidationResult(null);
          setIsValidated(false);
        }
      } else {
        const trimmedNpi = npiNumber;
        setValidationFromNpi(false);
        // NPI tab: lookup by number only (organization NPI from registry).
        const npiLookupResult = await npiLookupService.lookupNPI({
          npi_number: trimmedNpi,
        });

        if (
          npiLookupResult.success &&
          (npiLookupResult.data?.results?.length ?? 0) > 0
        ) {
          // Process NPI lookup results
          const npiResults = npiLookupResult.data?.results ?? [];
          const npiData = npiResults[0];
          setNpiData(npiData);
          setNpiVerified(true);
          setNpiSuccessMessage(
            npiLookupResult.message || "NPI lookup successful",
          );
          setNpiError(null);

          // Create validation result when we have at least a facility type (or backend left it ambiguous)
          const hasFacilityType =
            npiData.facility_type && npiData.facility_type.id;
          const facilityTypeConfirmed =
            (npiData as any).facility_type_confirmed === true;

          if (hasFacilityType || !facilityTypeConfirmed) {
            let rhcCatalogIdNpi: number | undefined;
            try {
              const catalog = await getFacilityTypes();
              rhcCatalogIdNpi = pickRhcFacilityTypeIdFromCatalog(
                catalog.facility_types || [],
              );
            } catch (e) {
              console.error("prefetch RHC facility_type_id (NPI) failed:", e);
            }
            const snfType = applySnfLookupTypeOverride({
              facility_type:
                npiData.facility_type?.display_name ||
                npiData.primary_taxonomy?.description ||
                "",
              facility_type_id: npiData.facility_type?.id ?? undefined,
              facility_type_slug: npiData.facility_type?.type_slug,
              primary_taxonomy: npiData.primary_taxonomy,
              site_name: (npiData as any).site_name,
              organization_name: npiData.organization_name,
            });
            const rhcType = applyRhcLookupTypeOverride({
              facility_type: snfType.facility_type,
              facility_type_id: snfType.facility_type_id,
              facility_type_slug: snfType.facility_type_slug,
              primary_taxonomy: npiData.primary_taxonomy,
              ccn: (npiData as any).ccn,
              ccn_number: (npiData as any).ccn_number ?? (npiData as any).ccn,
              match_method: (npiData as any).facility_type?.match_method,
              facility_type_obj: npiData.facility_type,
              site_name: (npiData as any).site_name,
              organization_name: npiData.organization_name,
            });
            const typedFacility = withRhcCatalogTypeId(
              rhcType.facility_type_slug === "rural_health_clinic" ? rhcType : snfType,
              rhcCatalogIdNpi,
            );
            const mappedFacility = {
              site_name:
                ((npiData as any).site_name && String((npiData as any).site_name).trim()) ||
                npiData.organization_name ||
                npiData.basic?.organization_name ||
                facilityName,
              city:
                npiData.primary_address?.city ||
                npiData.addresses?.[0]?.city ||
                City,
              state:
                npiData.primary_address?.state ||
                npiData.addresses?.[0]?.state ||
                stateToSend,
              facility_type: typedFacility.facility_type,
              facility_type_id: typedFacility.facility_type_id,
              facility_type_slug: typedFacility.facility_type_slug,
              category: npiData.facility_type?.category,
              npi_number: npiData.npi_number,
              enumeration_type: (npiData as any).enumeration_type,
              organization_name:
                npiData.organization_name || npiData.basic?.organization_name,
              facility_type_confirmed:
                Boolean(typedFacility.facility_type_slug) || facilityTypeConfirmed,
              facility_type_obj: mergeLookupFacilityTypeObj(
                typedFacility,
                npiData.facility_type || undefined,
              ),
              primary_address: npiData.primary_address,
              ccn_number: (npiData as any).ccn_number ?? (npiData as any).ccn ?? undefined,
              ccn: (npiData as any).ccn,
              already_registered: isFacilityAlreadyRegistered(npiData),
            };

            const validationResult: FacilityValidationResult = {
              success: true,
              is_valid: true,
              facilities: [mappedFacility],
              suggestions: [],
              message: npiLookupResult.message || "Facility found",
            };

            setValidationResult(validationResult);
            setIsValidated(true);
            setValidationFromNpi(true);
          } else {
            // NPI lookup succeeded but facility_type is missing / unsupported.
            // Check if this is a physician/provider NPI based on taxonomy code
            const taxonomyCode = (npiData as any)?.primary_taxonomy?.code || "";
            const taxonomyDesc =
              (npiData as any)?.primary_taxonomy?.description ||
              (npiData as any)?.primary_taxonomy?.desc ||
              "";
            const taxonomyCodeText = taxonomyCode
              ? ` (Taxonomy Code: ${taxonomyCode})`
              : "";

            // Check if this is a physician taxonomy code (starts with 207, 208, 363, 364, etc.)
            const isPhysicianCode =
              taxonomyCode &&
              (taxonomyCode.startsWith("207") || // Allopathic & Osteopathic Physicians
                taxonomyCode.startsWith("208") || // Allopathic & Osteopathic Physicians
                taxonomyCode.startsWith("209") || // Allopathic & Osteopathic Physicians
                taxonomyCode.startsWith("204") || // Surgery specialties
                taxonomyCode.startsWith("206") || // Allopathic & Osteopathic Physicians
                taxonomyCode.startsWith("363") || // Physician Assistants
                taxonomyCode.startsWith("364") || // Nurse Practitioners
                taxonomyCode.startsWith("367")); // Certified Registered Nurse Anesthetist

            let facilityTypeErrorMessage: string;
            if (isPhysicianCode) {
              facilityTypeErrorMessage =
                `UniComply provides compliance analysis for healthcare facilities, not individual physicians or provider practices. ` +
                `This NPI belongs to "${taxonomyDesc}"${taxonomyCodeText}, which is a physician/provider specialty. ` +
                `Please search for a hospital, clinic, nursing facility, pharmacy, or other healthcare facility instead.`;
              setErrorTitle("Provider Type Not Supported");
            } else {
              facilityTypeErrorMessage =
                `This facility type is not currently supported${taxonomyCodeText}. ` +
                `Expansion of supported facility types is under review. Please revise your selection to proceed.`;
              setErrorTitle("Facility Type Not Supported");
              if (taxonomyCode) {
                void notifyUnsupportedFacilityType({
                  taxonomy_code: taxonomyCode,
                  taxonomy_display_name: taxonomyDesc || undefined,
                  facility_name:
                    npiData.organization_name ||
                    (npiData as any).basic?.organization_name ||
                    undefined,
                  npi_number: npiData.npi_number || undefined,
                  city:
                    npiData.primary_address?.city ||
                    npiData.addresses?.[0]?.city ||
                    undefined,
                  state:
                    npiData.primary_address?.state ||
                    npiData.addresses?.[0]?.state ||
                    undefined,
                  country: getSelectedCountryCode(),
                }).catch((err) =>
                  console.error(
                    "notify unsupported facility type (NPI path) failed:",
                    err,
                  ),
                );
              }
            }

            setNpiError(facilityTypeErrorMessage);
            setError(facilityTypeErrorMessage);
            setNpiVerified(false);
            setValidationFromNpi(false);
            setValidationResult(null);
            setIsValidated(false);
          }

          // Store session data
          const nameForSession =
            ((npiData as any).site_name && String((npiData as any).site_name).trim()) ||
            npiData.organization_name ||
            (npiData as any).basic?.organization_name ||
            facilityName ||
            "";
          sessionStorage.setItem("sessionState", selectedState);
          sessionStorage.setItem("sessionCity", City);
          sessionStorage.setItem("sessionFacilityName", nameForSession);
          sessionStorage.setItem("sessionNpiNumber", trimmedNpi);
          sessionStorage.setItem("sessionPhoneNumber", effectivePhoneNumber);
        } else {
          // Extract error information
          let errorCode: string | null = null;
          let apiErrorMessage: string | null = null;

          if (npiLookupResult.error) {
            if (
              typeof npiLookupResult.error === "object" &&
              npiLookupResult.error !== null
            ) {
              // Error is an object with code and message properties
              errorCode = (npiLookupResult.error as any).code || null;
              apiErrorMessage = (npiLookupResult.error as any).message || null;
            } else if (typeof npiLookupResult.error === "string") {
              // Error is a string
              apiErrorMessage = npiLookupResult.error;
            }
          } else if (npiLookupResult.message) {
            apiErrorMessage = npiLookupResult.message;
          }

          // Show generic message only for LOCATION_MISMATCH error, otherwise show actual error message
          let errorMessage: string;
          if (
            errorCode === "LOCATION_MISMATCH" ||
            (apiErrorMessage && apiErrorMessage.includes("Location mismatch"))
          ) {
            errorMessage =
              "Facility validation failed. Please verify your NPI number, city, and state information and try again.";
          } else {
            errorMessage =
              apiErrorMessage ||
              `No results found for NPI number ${trimmedNpi}`;
          }

          setNpiError(errorMessage);
          setNpiVerified(false);
          setNpiData(null);
          setErrorTitle("Validation Error");
          // Also set the general error state to display the error message
          setError(errorMessage);
        }
      }
    } catch (err) {
      console.error("Validation failed:", err);
      const msg =
        err instanceof Error ? err.message : "Facility validation failed.";
      if (searchTab === "npi") {
        setError(
          msg.includes("timed out")
            ? msg
            : "Facility validation failed. Please enter a valid NPI number.",
        );
      } else {
        setError(
          msg.includes("timed out")
            ? msg
            : "Facility search failed. Check your connection and try again.",
        );
      }
    } finally {
      setLoading(false);
      setLoadingMessage(null);
      sessionStorage.removeItem("facilitySearchInProgress");
    }
  };

  const handleFacilitySelect = (
    event: SelectChangeEvent<unknown> | any,
    facilityObj?: any,
  ) => {
    let value: string;
    let selectedObj: any;

    // Handle both dropdown event and direct facility object
    if (facilityObj) {
      // Called from card selection with facility object
      value = facilityObj.site_name;
      selectedObj = facilityObj;
    } else {
      // Called from dropdown with event
      value = event.target.value as string;
      selectedObj = validationResult?.is_valid
        ? validationResult?.facilities?.find((f: any) => f.site_name === value)
        : validationResult?.suggestions?.find(
            (s: any) => s.site_name === value,
          );
    }

    // Store the complete facility object, not just the name
    setSelectedFacility(selectedObj?.site_name || value);

    if (selectedObj) {
      sessionStorage.setItem(
        "filtered_selectedFacility",
        JSON.stringify(selectedObj),
      );
      setCity(selectedObj.city);
      setFacilityName(selectedObj.site_name);
      const stateData = states.find(
        (state) =>
          (state.state_abbreviation || "").toUpperCase() ===
          String(selectedObj.state || "").toUpperCase(),
      );
      const abbr =
        stateData?.state_abbreviation?.toUpperCase() ||
        String(selectedObj.state || "")
          .trim()
          .toUpperCase()
          .slice(0, 8);
      if (abbr) {
        setSelectedState(abbr);
        sessionStorage.setItem("sessionState", abbr);
      }
      // Store in sessionStorage - use facility's NPI number if available, otherwise use state NPI
      // Store actual NPI for API calls, but don't update the form field
      const facilityNpiToStore = selectedObj.npi_number || npiNumber;
      sessionStorage.setItem("sessionFacilityName", selectedObj.site_name);
      sessionStorage.setItem("sessionCity", selectedObj.city);
      sessionStorage.setItem("sessionNpiNumber", facilityNpiToStore);
      sessionStorage.setItem(
        "sessionPhoneNumber",
        phoneNumber.trim() || "0123456789",
      );
    }
    setValidationResult((prev: any) => {
      if (!prev) return prev;

      const updatedResult = prev.is_valid
        ? {
            ...prev,
            facilities: prev.facilities?.map((f: any) => ({
              ...f,
              selected: f.site_name === value,
            })),
          }
        : {
            ...prev,
            suggestions: prev.suggestions?.map((s: any) => ({
              ...s,
              selected: s.site_name === value,
            })),
          };

      // Call handleContinue with the updated result
      setTimeout(() => {
        const selectedFacilityObj = getSelectedFacility(updatedResult);

        if (selectedFacilityObj) {
          // Derive a clean numeric facility_type_id from the selected facility object
          const facilityTypeIdFromObj =
            (selectedFacilityObj as any)?.facility_type_obj?.id ??
            (selectedFacilityObj as any)?.facility_type_id ??
            null;

          const selectedFacilityToStore = {
            ...selectedFacilityObj,
            // Ensure we always persist the numeric ID (if available)
            facility_type_id: facilityTypeIdFromObj,
          };

          sessionStorage.setItem(
            "filtered_selectedFacility",
            JSON.stringify(selectedFacilityToStore),
          );
          sessionStorage.setItem(
            "filtered_category",
            selectedFacilityObj.category,
          );
          sessionStorage.setItem(
            "filtered_facilityTypes",
            selectedFacilityObj.facility_type,
          );
        }

        if (onValidate) {
          const facilityData: FacilityData = {
            state: selectedState,
            city: City,
            facilityName: value,
            country: getSelectedCountryCode(),
          };
          sessionStorage.setItem("DisplayPage", "false");
          onValidate(facilityData);
        }
      }, 50);

      return updatedResult;
    });
  };

  // Handler for facility card click - calls API before navigating to pricing
  const handleFacilityCardClick = async (facility: any) => {
    if (isFacilityAlreadyRegistered(facility, ownedFacilityKeys)) {
      return;
    }
    if (associationClickInFlightRef.current) {
      return;
    }
    associationClickInFlightRef.current = true;
    // Show in-progress immediately so PW/users do not treat idle CMS card as
    // "association never started" while check/create run (CI 33332 ~32s Mackay).
    // flushSync: paint overlay before any await (RHC catalog / check+UPF).
    flushSync(() => {
      setLoading(true);
      // Multi unlocked Add path creates UPF + cart — not verification.
      setLoadingMessage(
        multiFacilityUnlocked || userIsSalesUser() ? "adding" : "plans",
      );
    });
    try {
    try {
      sessionStorage.removeItem("preferred_facility_for_pricing");
      // Trial only — multi/cart Add must not seed verification pending.
      if (!multiFacilityUnlocked && !userIsSalesUser()) {
        sessionStorage.setItem(
          "current_verification_facility",
          JSON.stringify({
            user_preferred_facility_id: null,
            facility_name: facility?.site_name || "",
            city: facility?.city || "",
            state: facility?.state || "",
            ccn_number: String(
              (facility as any)?.ccn_number || (facility as any)?.ccn || "",
            ),
            facility_type: facility?.facility_type || "",
          }),
        );
      } else {
        sessionStorage.removeItem("current_verification_facility");
      }
    } catch {
      // ignore storage failures
    }
    // Check if facility_type object is present with id
    // Check both facility_type_obj (full object) and facility_type_id (just the id)
    let facilityForAssociate = facility;
    let hasFacilityType =
      (facility?.facility_type_obj && facility.facility_type_obj.id) ||
      facility?.facility_type_id;
    // Mackay/Idaho RHC: applyRhcLookupTypeOverride forces Rural Health Clinic but
    // clears physician facility_type_id. CMS POS cards often have no RHC catalog id
    // either — without resolving one, association never leaves the CMS card
    // (CI 33092 leftover Verification pending "133990", never Select Plan).
    if (!hasFacilityType && isRhcLookupFacilityForAssociation(facility)) {
      try {
        const catalog = await getFacilityTypes();
        const rhcId = pickRhcFacilityTypeIdFromCatalog(
          catalog.facility_types || [],
        );
        if (rhcId) {
          facilityForAssociate = {
            ...facility,
            facility_type: RHC_CHECKOUT_DISPLAY_NAME,
            facility_type_id: rhcId,
            facility_type_slug: RHC_CHECKOUT_SLUG,
            facility_type_obj: {
              ...(facility?.facility_type_obj || {}),
              id: rhcId,
              display_name: RHC_CHECKOUT_DISPLAY_NAME,
              type_slug: RHC_CHECKOUT_SLUG,
            },
          };
          hasFacilityType = true;
        }
      } catch (e) {
        console.error("resolve RHC facility_type_id from catalog failed:", e);
      }
    }
    if (!hasFacilityType) {
      const taxonomyCode = String(
        facility?.primary_taxonomy?.code ?? "",
      ).trim();
      setAssociationDialogTitle("Facility type not supported");

      const codeLabel = taxonomyCode || "This facility type";
      if (taxonomyCode) {
        setAssociationDialogMessage(
          `Facility type ${taxonomyCode} is not yet supported. Your report has been submitted; our team will review it and may follow up.`,
        );
        void notifyUnsupportedFacilityType({
          taxonomy_code: taxonomyCode,
          taxonomy_display_name:
            facility?.primary_taxonomy?.description ||
            facility?.primary_taxonomy?.desc ||
            undefined,
          facility_name:
            facility?.site_name ||
            facility?.organization_name ||
            undefined,
          npi_number: facility?.npi_number || undefined,
          city: facility?.city || undefined,
          state: facility?.state || undefined,
          country: getSelectedCountryCode(),
        }).catch((e) =>
          console.error("notify unsupported facility type failed:", e),
        );
      } else {
        setAssociationDialogMessage(
          `${codeLabel} is not yet supported in Unicomply. Please contact the customer support team if you need access.`,
        );
      }
      setAssociationDialogOpen(true);
      return;
    }
    facility = facilityForAssociate;
    // NPPES card click must persist type so Pricing Pay Now still has it
    // when leftover hydrate leaves filtered_selectedFacility unset.
    try {
      const facilityTypeIdFromObj =
        facility?.facility_type_obj?.id ?? facility?.facility_type_id ?? null;
      sessionStorage.setItem(
        "filtered_selectedFacility",
        JSON.stringify({
          ...facility,
          site_name: facility?.site_name || facility?.facility_name || "",
          facility_type:
            facility?.facility_type ||
            facility?.facility_type_obj?.display_name ||
            "",
          facility_type_id: facilityTypeIdFromObj,
        }),
      );
    } catch {
      // Pricing can still resolve type from preferred / leftover.
    }
    // Unique id is always NPI. Optional CMS CCN is a separate field, never facility_unique_id.
    const facilityNpi = String(facility?.npi_number || facility?.npi || npiNumber.trim() || "").trim();
    const trimmedAssociationId = facilityNpi;
    // Identifier optional: if not provided, skip association check and go straight to pricing
    if (!trimmedAssociationId) {
      setNpiError(null);
      handleFacilitySelect(null, facility);
      navigate("/dashboard/facilities", {
        state: { currentView: "orderSummary", fromFacilityValidation: true },
      });
      return;
    }

    // Prepare data for API call FIRST, before any other operations
    const npiNumberValue = trimmedAssociationId;
    const facilityNpiForDetails = facilityNpi;
    const facilityName = facility?.site_name || "";
    const city = facility?.city || "";
    const state = facility?.state || "";
    const country = getSelectedCountryCode();
    const facilityType = facility?.facility_type || "";
    // Prefer bare Medicare CCN for UPF details / verification chrome (Mackay 133990).
    // Association payload still uses Rules-safe npiNumberValue when CCN is short.
    const registryIds = resolveFacilityRegistryDisplayIds({
      ccn_number:
        (facility as any)?.ccn_number || (facility as any)?.ccn || undefined,
      facility_unique_id: (facility as any)?.facility_unique_id,
      npi_number:
        facilityNpiForDetails ||
        (facility as any)?.npi_number ||
        (facility as any)?.npi,
      country_code: country,
    });
    const detailsCcn =
      registryIds.ccn && registryIds.ccn !== npiNumberValue
        ? registryIds.ccn
        : undefined;

    // Active trial only: track verification pending during check+UPF (CI 33387).
    // Expired/unlocked multi path must NOT set this — Dashboard hijacks to pricing.
    try {
      sessionStorage.removeItem("preferred_facility_for_pricing");
      if (!multiFacilityUnlocked && !userIsSalesUser()) {
        sessionStorage.setItem(
          "current_verification_facility",
          JSON.stringify({
            user_preferred_facility_id: null,
            facility_name: facilityName || "",
            city: city || "",
            state: state || "",
            ccn_number: npiNumberValue || "",
            medicare_ccn: detailsCcn,
            facility_type: facilityType || "",
          }),
        );
      } else {
        sessionStorage.removeItem("current_verification_facility");
      }
    } catch {
      // ignore storage failures
    }

    // Call the check-user-facility-association API BEFORE handling facility selection
    if (facilityName) {
      try {
        const associationPayload = {
          ccn_number: npiNumberValue,
          medicare_ccn: detailsCcn,
          facility_name: facilityName,
          city: city,
          state: state,
          country: country,
          facility_type: facilityType,
        };

        console.log(
          "🔍 Calling check-user-facility-association API with payload:",
          associationPayload,
        );
        const response = await checkUserFacilityAssociation(associationPayload);
        console.log("📥 Full API Response:", JSON.stringify(response, null, 2));
        console.log("📥 response.data:", response.data);
        console.log(
          "📥 response.data?.can_associate:",
          response.data?.can_associate,
        );
        console.log(
          "📥 Type of can_associate:",
          typeof response.data?.can_associate,
        );

        // Check if association is allowed - handle both boolean false and string "false"
        const canAssociate = response.data?.can_associate;
        // Convert to string for comparison to handle both boolean and string values
        const canAssociateStr = String(canAssociate);
        const canAssociateBool =
          canAssociate === true || canAssociateStr === "true";
        const canAssociateFalse =
          canAssociate === false ||
          canAssociateStr === "false" ||
          canAssociateStr === "0";

        console.log("🔍 canAssociateBool:", canAssociateBool);
        console.log("🔍 canAssociateFalse:", canAssociateFalse);

        if (canAssociateFalse) {
          // If the user is already associated with this facility (e.g. just completed checkout),
          // navigate them in rather than showing a blocking error dialog.
          // if (response.data?.is_already_associated) {
          //   console.log("✅ User already associated with facility — navigating directly");
          //   handleFacilitySelect(null, facility);
          //   navigate("/dashboard/facilities", {
          //     state: { currentView: "pricing" },
          //   });
          //   return;
          // }
          // Show popup with the API message
          const errorMessage =
            response.message ||
            "This facility cannot be associated with your account.";
          console.log("❌ Association NOT allowed - can_associate is false");
          console.log("❌ Setting dialog message:", errorMessage);
          setAssociationDialogTitle("Facility already set up");
          setAssociationDialogMessage(errorMessage);
          setAssociationDialogOpen(true);
          try {
            sessionStorage.removeItem("current_verification_facility");
          } catch {
            // ignore storage failures
          }
          return;
        }

        // If can_associate is true, first create user preferred facility, then show verification popup or (sales bypass) go straight to app
        if (canAssociateBool) {
          console.log(
            "✅ Facility association allowed, creating user preferred facility before verification popup",
          );

          try {
            const currentUserId = Number(getCurrentUserId() || "0");
            const user = getCurrentUser();
            const username = user?.first_name && user?.last_name
              ? `${user.first_name} ${user.last_name}`
              : user?.display_name || user?.first_name || "";
            const canSalesBypass = userIsSalesUser();

            if (currentUserId) {
              // Resolve facility_type_id, preferring the facility object used for this click,
              // and falling back to what's stored in session (if any).
              let facilityTypeId: number | undefined;

              // 1) Prefer the facility object from the current card click
              const facilityTypeIdFromFacility =
                (facility as any)?.facility_type_obj?.id ??
                (facility as any)?.facility_type_id ??
                null;

              if (facilityTypeIdFromFacility != null) {
                facilityTypeId = Number(facilityTypeIdFromFacility);
              } else {
                // 2) Fallback: Try to get facility_type_id from the selected facility (stored in session)
                try {
                  const stored = sessionStorage.getItem(
                    "filtered_selectedFacility",
                  );
                  if (stored) {
                    const parsed = JSON.parse(stored);
                    if (parsed?.facility_type_id) {
                      facilityTypeId = Number(parsed.facility_type_id);
                    }
                  }
                } catch {
                  // ignore parse errors, just skip facility_type_id
                }
              }

              const pa = (facility as any)?.primary_address;
              const street1 =
                (pa?.address_1 as string | undefined) ||
                (facility as any)?.address_1;
              const street2 =
                (pa?.address_2 as string | undefined) ||
                (facility as any)?.address_2;
              const postalFromNpi =
                (pa?.postal_code as string | undefined) ||
                (facility as any)?.postal_code;

              const primaryTax = (facility as any)?.primary_taxonomy;
              const primaryTaxonomyCode =
                typeof primaryTax?.code === "string"
                  ? primaryTax.code.trim() || undefined
                  : undefined;
              const primaryTaxonomyDescRaw =
                primaryTax?.description || primaryTax?.desc;
              const primaryTaxonomyDesc =
                typeof primaryTaxonomyDescRaw === "string"
                  ? primaryTaxonomyDescRaw.trim() || undefined
                  : undefined;

              // Keep facility_details.ccn_number Rules-safe (NPI / username_* id) for mint +
              // uniqueness. Bare Medicare CCN (133990) in ccn_number dual-mints NPI+CCN twins
              // and breaks AB#10155 Save Profile reclaim (CI 33370 after 5866).
              const facilityDetails = {
                user_id: String(currentUserId),
                ccn_number: npiNumberValue || undefined,
                medicare_ccn:
                  registryIds.ccn &&
                  registryIds.ccn !== npiNumberValue
                    ? registryIds.ccn
                    : undefined,
                npi_number: facilityNpiForDetails || registryIds.npi || undefined,
                facility_name: facilityName || undefined,
                facility_type: facilityType || undefined,
                facility_type_id: facilityTypeId,
                city: city || undefined,
                state: state || undefined,
                country: country || undefined,
                address_1:
                  typeof street1 === "string" && street1.trim()
                    ? street1.trim()
                    : undefined,
                address_2:
                  typeof street2 === "string" && street2.trim()
                    ? street2.trim()
                    : undefined,
                postal_code:
                  typeof postalFromNpi === "string" && postalFromNpi.trim()
                    ? postalFromNpi.trim()
                    : undefined,
                phone_number: phoneNumber.trim() || undefined,
                skip_npi_validation: !npiNumberValue || npiNumberValue.trim() === "",
                username: username || undefined,
                pricing_tier_id: 0,
                bypass_checkout: canSalesBypass,
                discount_coupon: "",
                primary_taxonomy_code: primaryTaxonomyCode,
                primary_taxonomy_desc: primaryTaxonomyDesc,
              };

              // Active trial (multiFacilityUnlocked=false): always unverified →
              // verification page. Multi unlocked / sales bypass → cart path.
              const createAsVerified =
                canSalesBypass || multiFacilityUnlocked;

              console.log(
                "📤 Calling createUserPreferredFacility with payload:",
                {
                  user_id: currentUserId,
                  facility_details: facilityDetails,
                  is_verified: createAsVerified,
                },
              );

              // Save phone in background — never block association/UPF create on
              // profile PATCH latency (CI 33332 Mackay click→UPF ~32s vs Evergreen ~1.2s).
              const currentUser = getCurrentUser();
              if (phoneNumber.trim() && !currentUser?.phone_number) {
                void updateProfile({ phone_number: phoneNumber.trim() }).catch(
                  () => {},
                );
              }

              const response = await createUserPreferredFacility({
                user_id: currentUserId,
                facility_details: facilityDetails,
                is_verified: createAsVerified,
              });
              
              // Immediately fetch updated preferred facilities to refresh the list
              // This prevents the verification screen from appearing
              
              const createdPreferredFacility = response?.data as
                | (NonNullable<typeof response.data> & {
                    has_existing_facilities?: boolean;
                    existing_facility_count?: number;
                  })
                | undefined;
              // Expired trial / paid / sales bypass → multi cart. Active trial → verify.
              const skipVerificationPage =
                canSalesBypass || multiFacilityUnlocked;
              if (skipVerificationPage) {
                const facilityId = createdPreferredFacility?.id;
                try {
                  // Do not leave verification track — Dashboard treats it as
                  // pending/verified and redirects to Select Plan.
                  sessionStorage.removeItem("current_verification_facility");

                  const facilityDetailsToStore = {
                    facility_name: facilityName || "",
                    facility_type: facilityType || "",
                    facility_type_id: facilityTypeId,
                    city: city || undefined,
                    state: state || undefined,
                    country: country || undefined,
                    ccn_number: npiNumberValue || undefined,
                    medicare_ccn:
                      registryIds.ccn && registryIds.ccn !== npiNumberValue
                        ? registryIds.ccn
                        : undefined,
                    phone_number: phoneNumber.trim() || undefined,
                    primary_taxonomy_code: primaryTaxonomyCode,
                    primary_taxonomy_desc: primaryTaxonomyDesc,
                  };

                  const cartKey = facilityCartKeyFromDetails({
                    ccn_number: npiNumberValue || undefined,
                    facility_name: facilityName || undefined,
                    city: city || undefined,
                    state: state || undefined,
                  });
                  try {
                    // Cart first so Dashboard skip-pricing sees items immediately.
                    const nextCart = addFacilityToCart(
                      {
                        cart_key: cartKey,
                        id: facilityId,
                        user_id: currentUserId,
                        is_verified: true,
                        is_active: true,
                        facility_name: facilityName || "",
                        facility_type: facilityType || "",
                        facility_type_id: facilityTypeId,
                        ccn_number: npiNumberValue || undefined,
                        city: city || undefined,
                        state: state || undefined,
                        country: country || undefined,
                        phone_number: phoneNumber.trim() || undefined,
                        facility_details: facilityDetailsToStore,
                      },
                      existingActiveFacilityCount,
                      multiSelectEnabled,
                      canSalesBypass,
                    );
                    setFacilityCart(nextCart);
                    // Cart must not leave preferred_facility_for_pricing set —
                    // that key alone opens Select Plan.
                    sessionStorage.removeItem("preferred_facility_for_pricing");
                    toast.success(
                      multiSelectEnabled
                        ? `${facilityName || "Facility"} added. Select more or review order.`
                        : `${facilityName || "Facility"} added. Review order to continue.`,
                    );
                  } catch (cartErr) {
                    if (facilityId != null) {
                      const orphanId = Number(facilityId);
                      if (Number.isFinite(orphanId) && orphanId > 0) {
                        void softDeleteUserPreferredFacility(orphanId).catch(
                          () => {},
                        );
                      }
                    }
                    toast.error(
                      cartErr instanceof Error
                        ? cartErr.message
                        : `Facility limit is ${cartCap}`,
                    );
                    setLoading(false);
                    setLoadingMessage(null);
                    return;
                  }
                } catch {
                  // ignore storage errors
                }

                setLoading(false);
                setLoadingMessage(null);
                return;
              }

              setLoading(false);
              setLoadingMessage(null);

              // Normal flow: verification pending
              // Optional: check if backend indicates existing facilities.
              const hasExistingFlagPresent =
                createdPreferredFacility &&
                (typeof createdPreferredFacility.has_existing_facilities ===
                  "boolean" ||
                  typeof createdPreferredFacility.existing_facility_count ===
                    "number");

              if (hasExistingFlagPresent) {
                const existing =
                  createdPreferredFacility.has_existing_facilities === true ||
                  (createdPreferredFacility.existing_facility_count ?? 0) > 0;
                setHasExistingFacilities(existing);
              }

              // Stay on /dashboard/facilities with verificationPending state (no facilities/verificationPending route;
              // CI 33374 Evergreen/SNF/Idaho 404'd that path after 5868 and never reached Select Plan).
              try {
                sessionStorage.removeItem("preferred_facility_for_pricing");
                sessionStorage.setItem(
                  "current_verification_facility",
                  JSON.stringify({
                    user_preferred_facility_id:
                      typeof createdPreferredFacility?.id === "number"
                        ? createdPreferredFacility.id
                        : null,
                    facility_name: facilityName || "",
                    city: city || "",
                    state: state || "",
                    ccn_number: npiNumberValue || "",
                    medicare_ccn: detailsCcn,
                    facility_type: facilityType || "",
                  })
                );
              } catch {
                // ignore storage failures
              }

              navigate("/dashboard/facilities", {
                state: {
                  currentView: "verificationPending",
                  skipHomeRedirect: true,
                  refreshFacilities: true,
                  selectedFacility: {
                    facility_name: facilityName,
                    city: city,
                    state: state,
                    ccn_number: npiNumberValue,
                    medicare_ccn: detailsCcn,
                    facility_type: facilityType,
                  },
                },
              });
            } else {
              console.warn(
                "⚠️ Missing user_id, cannot create user preferred facility",
                { currentUserId },
              );
              setLoading(false);
              setLoadingMessage(null);
              toast.error(
                "We couldn't save this facility as your preferred one. Please try again.",
              );
              // Do not open verification popup if we couldn't create preferred facility
              return;
            }
          } catch (e: any) {
            console.error(
              "⚠️ Failed to create user preferred facility:",
              e,
            );
            
            setLoading(false);
            setLoadingMessage(null);

            // Check if error message contains "Entry already present"
            let errorMessage = e?.message || 
              "We couldn't save this facility as your preferred one. Please try again.";
            
            // Try to parse error message if it's JSON
            try {
              if (typeof e?.message === 'string' && e.message.startsWith('{')) {
                const parsed = JSON.parse(e.message);
                errorMessage = parsed?.message || parsed?.detail || errorMessage;
              }
            } catch (parseError) {
              // If parsing fails, use original message
            }
            
            // Check if error indicates entry already exists
            if (errorMessage.toLowerCase().includes("entry already present") ||
                errorMessage.toLowerCase().includes("already present for this user")) {
              // Idempotent resume: preferred row exists — continue mid-signup instead of
              // leaving the CMS card screen (CI 33040 Idaho stuck Verification pending).
              try {
                sessionStorage.setItem(
                  "current_verification_facility",
                  JSON.stringify({
                    user_preferred_facility_id: null,
                    facility_name: facilityName || "",
                    city: city || "",
                    state: state || "",
                    ccn_number: npiNumberValue || "",
                    medicare_ccn: detailsCcn,
                    facility_type: facilityType || "",
                  }),
                );
              } catch {
                // ignore storage failures
              }
              navigate("/dashboard/facilities", {
                state: {
                  currentView: "verificationPending",
                  skipHomeRedirect: true,
                  refreshFacilities: true,
                  selectedFacility: {
                    facility_name: facilityName,
                    city: city,
                    state: state,
                    ccn_number: npiNumberValue,
                    medicare_ccn: detailsCcn,
                    facility_type: facilityType,
                  },
                },
              });
              return;
            } else {
              toast.error(errorMessage);
            }
            // On failure, do not open verification popup
            return;
          }
        } else {
          // If can_associate is not explicitly true or false, default to showing error
          console.warn(
            "⚠️ Unexpected can_associate value:",
            canAssociate,
            "Type:",
            typeof canAssociate,
          );
          const errorMessage =
            response.message ||
            "Unable to associate with this facility. Please try again.";
          setAssociationDialogTitle("Unable to associate");
          setAssociationDialogMessage(errorMessage);
          setAssociationDialogOpen(true);
          // DO NOT navigate
          return;
        }
      } catch (error) {
        console.error("❌ Failed to check user facility association:", error);
        setAssociationDialogTitle("Unable to verify association");
        setAssociationDialogMessage(
          "We could not verify whether this facility can be added to your account. Check your connection and try again, or contact customer support.",
        );
        setAssociationDialogOpen(true);
        // DO NOT navigate on error
        return;
      }
    } else {
      // If no facility name, navigate directly (fallback)
      console.warn("⚠️ No facility name provided, navigating directly");
      handleFacilitySelect(null, facility);
      navigate("/dashboard/facilities", {
        state: { currentView: "orderSummary", fromFacilityValidation: true },
      });
    }
    } finally {
      associationClickInFlightRef.current = false;
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleCancel = () => {
    setIsValidated(false);
    setValidationResult(null);
    setSelectedFacility("");
    setError(null);
    setNpiData(null);
    setNpiError(null);
    setNpiSuccessMessage(null);
    setValidationFromNpi(false);

    // Also clear any legacy success/error content rendered by facility-context.js
    const legacyResultsDiv =
      typeof document !== "undefined"
        ? document.getElementById("validationResults")
        : null;
    if (legacyResultsDiv) {
      legacyResultsDiv.style.display = "none";
      legacyResultsDiv.innerHTML = "";
    }
  };

  /** Clear results/name for next search; keep cart, phone, state. */
  const handleSearchForAnotherFacility = () => {
    handleCancel();
    setFacilityName("");
    setNpiNumber("");
    setNpiVerified(false);
  };

  /** US NPPES: enumeration_type NPI-1 / NPI-2 → "CMS Type"; other countries → "Healthcare Type". */
  const isUnitedStatesFacility = (facility: any): boolean => {
    const fromRow = String(
      facility?.country_code ?? facility?.country ?? "",
    )
      .trim()
      .toUpperCase();
    if (fromRow) {
      return (
        fromRow === "USA" ||
        fromRow === "US" ||
        fromRow === "UNITED STATES" ||
        fromRow === "UNITED STATES OF AMERICA"
      );
    }
    const sel = (getSelectedCountryCode() || "USA").toUpperCase();
    return sel === "USA" || sel === "US";
  };

  const getCmsTypeEnumerationLabel = (facility: any): string => {
    const isUs = isUnitedStatesFacility(facility);
    const prefix = isUs ? "CMS Type" : "Healthcare Type";
    const raw = String(facility?.enumeration_type ?? "").trim().toUpperCase();
    if (!raw) return `${prefix}:`;
    if (raw.includes("NPI-2") || raw === "2") return `${prefix} 2:`;
    if (raw.includes("NPI-1") || raw === "1") return `${prefix} 1:`;
    return `${prefix}:`;
  };

  /**
   * Full address / region line for non-US facility cards (street when present, else city/province/postal/country).
   */
  const getNonUsFacilityAddressDisplay = (facility: any): string | null => {
    if (isUnitedStatesFacility(facility)) return null;
    const full = String((facility as { full_address?: string })?.full_address ?? "").trim();
    if (full) return full;
    const pa = facility?.primary_address;
    const a1 = String(pa?.address_1 ?? facility?.address_1 ?? "").trim();
    if (a1) {
      const a2 = String(pa?.address_2 ?? facility?.address_2 ?? "").trim();
      const pc = String(pa?.postal_code ?? facility?.postal_code ?? "").trim();
      const city = String(pa?.city ?? facility?.city ?? "").trim();
      const st = String(pa?.state ?? facility?.state ?? "").trim();
      let s = a1;
      if (a2) s += `, ${a2}`;
      const tail = [city, st].filter(Boolean).join(", ");
      if (tail) s += `, ${tail}`;
      if (pc) s += ` ${pc}`;
      return s.trim() || a1;
    }
    const city = String(facility?.city ?? "").trim();
    const st = String(facility?.state ?? "").trim();
    const pc = String(
      facility?.postal_code ?? facility?.primary_address?.postal_code ?? "",
    ).trim();
    const co = String(
      facility?.country ?? facility?.country_code ?? "",
    ).trim();
    const line = [city, st].filter(Boolean).join(", ");
    const rest = [pc || null, co || null].filter(Boolean).join(", ");
    if (!line && !rest) return null;
    return rest ? `${line}${line ? ", " : ""}${rest}` : line;
  };

  // Helper to get details for a facility (type label, CCN, ownership, location) for card display.
  const getFacilityDetails = (facility: any) => {
    const addr =
      facility.primary_address ||
      (facility.address_1 || facility.city
        ? {
            address_1: facility.address_1,
            address_2: facility.address_2,
            city: facility.city,
            state: facility.state,
            postal_code: facility.postal_code,
          }
        : null);
    const locationStr =
      facility.full_address ||
      (addr
        ? [addr.address_1, addr.address_2, addr.city, addr.state, addr.postal_code]
            .filter(Boolean)
            .join(", ")
        : facility.city && facility.state
          ? `${facility.city}, ${facility.state}`
          : "");
    let outLocation = locationStr;
    if (outLocation && !isUnitedStatesFacility(facility)) {
      const co = String(facility?.country ?? facility?.country_code ?? "").trim();
      if (
        co &&
        !outLocation.toLowerCase().includes(co.toLowerCase())
      ) {
        outLocation = `${outLocation}, ${co}`;
      }
    }
    const ccn =
      (facility as any).ccn_number ??
      (facility as any).ccn ??
      "";
    const facilityType =
      typeof facility.facility_type === "string"
        ? facility.facility_type
        : (facility.facility_type as any)?.display_name ??
          (facility.facility_type as any)?.displayName ??
          "";
    const ownership =
      (facility as any).ownership != null && String((facility as any).ownership).trim() !== ""
        ? (facility as any).ownership
        : null;
    const cmsTypeLabel = getCmsTypeEnumerationLabel(facility);
    return {
      locationStr: outLocation,
      ccn,
      facilityType,
      ownership,
      cmsTypeLabel,
    };
  };

  // Common renderer for facility cards so NPI lookup and legacy validation
  // share the exact same UI and scroll behaviour. Each card shows full details (US: CMS type; non-US: healthcare type, CCN, ownership, location).
  const renderFacilityCards = (facilities: any[] | null | undefined) => {
    if (!facilities || facilities.length === 0) return null;

    const sortedFacilities = [...facilities].sort((a, b) => {
      const aCcn = (a.ccn_number ?? a.ccn ?? "").toString().trim();
      const bCcn = (b.ccn_number ?? b.ccn ?? "").toString().trim();
      if (aCcn && !bCcn) return -1;
      if (!aCcn && bCcn) return 1;
      return 0;
    });

    const groupKeysOrdered: string[] = [];
    const seenKey = new Set<string>();
    for (const f of sortedFacilities) {
      const k = facilityAddressGroupingKey(f as Record<string, unknown>);
      if (!seenKey.has(k)) {
        seenKey.add(k);
        groupKeysOrdered.push(k);
      }
    }
    const groupsByAddress = new Map<string, typeof sortedFacilities>();
    for (const f of sortedFacilities) {
      const k = facilityAddressGroupingKey(f as Record<string, unknown>);
      const arr = groupsByAddress.get(k);
      if (arr) arr.push(f);
      else groupsByAddress.set(k, [f]);
    }

    return (
      <FacilityDropdownSection>
        <FacilityDropdownHeader
          sx={{
            width: "100%",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <BuildingIcon />
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: "16px",
                color: "#065b70",
              }}
            >
              {facilities.length === 1
                ? "1 facility matches your search"
                : `${facilities.length} facilities match your search`}
            </Typography>
          </Box>
          {multiSelectEnabled ? (
            <Button
              type="button"
              size="small"
              onClick={handleSearchForAnotherFacility}
              sx={{
                textTransform: "none",
                color: "#0a829d",
                fontWeight: 600,
                fontFamily: "Rethink Sans, sans-serif",
                px: 1,
                "&:hover": { bgcolor: "#f0fdfa" },
              }}
            >
              + Search for another facility
            </Button>
          ) : null}
        </FacilityDropdownHeader>

        <Box sx={{ padding: "8px 0" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "24px",
              width: "100%",
              alignItems: "stretch",
            }}
          >
          {groupKeysOrdered.map((groupKey) => {
            const groupFacilities = groupsByAddress.get(groupKey) ?? [];
            const showAddressBanner = groupFacilities.length > 1;
            const bannerLocation = showAddressBanner
              ? getFacilityDetails(groupFacilities[0]).locationStr
              : "";

            const groupCardNodes = groupFacilities.map((facility, index) => {
              const { locationStr, ccn, facilityType, ownership, cmsTypeLabel } =
                getFacilityDetails(facility);
              const isOwnFacility = isOwnedByCurrentUser(
                facility,
                ownedFacilityKeys,
              );
              const isAlreadyRegistered = isFacilityAlreadyRegistered(
                facility,
                ownedFacilityKeys,
              );
              const cardNpi = facility.npi_number ?? (facility as any).npi ?? "";
              const cardCartKey = facilityCartKeyFromDetails({
                ccn_number:
                  String(
                    facility.npi_number ??
                      (facility as any).npi ??
                      facility.ccn_number ??
                      "",
                  ) || undefined,
                facility_name: facility.site_name || undefined,
                city: facility.city || undefined,
                state: facility.state || undefined,
              });
              const inCart = facilityCart.some((c) => c.cart_key === cardCartKey);
              const cartFull = isSelfServeCartFull(
                existingActiveFacilityCount,
                facilityCart.length,
                multiSelectEnabled,
                userIsSalesUser(),
              );
              return (
                <Box
                  key={`${groupKey}-${index}-${facility.site_name ?? ""}-${cardNpi}`}
                  onClick={() => {
                    if (
                      isAlreadyRegistered ||
                      loading ||
                      associationClickInFlightRef.current
                    ) {
                      return;
                    }
                    // Multi unlocked: Add button only. Locked trial: whole-card click.
                    if (!multiSelectEnabled) {
                      void handleFacilityCardClick(facility);
                    }
                  }}
                  aria-disabled={isAlreadyRegistered || undefined}
                  sx={{
                    backgroundColor: isAlreadyRegistered
                      ? "#f8fafc"
                      : inCart
                        ? "#f0fdfa"
                        : "#ffffff",
                    border: isAlreadyRegistered
                      ? "1px solid #cbd5e1"
                      : inCart
                        ? "2px solid #0e7490"
                        : selectedFacility === facility.site_name
                          ? "2px solid #065b70"
                          : "1px solid #065b70",
                    borderRadius: "8px",
                    overflow: "hidden",
                    cursor: isAlreadyRegistered
                      ? "not-allowed"
                      : loading
                        ? "not-allowed"
                        : multiSelectEnabled
                          ? "default"
                          : "pointer",
                    opacity: isAlreadyRegistered ? 0.55 : loading ? 0.6 : 1,
                    boxShadow: isAlreadyRegistered
                      ? "none"
                      : "0px 4px 4px 0px rgba(0,0,0,0.05)",
                    "&:hover": {
                      backgroundColor: isAlreadyRegistered
                        ? "#f8fafc"
                        : inCart
                          ? "#f0fdfa"
                          : loading
                            ? "#ffffff"
                            : "#f5f5f5",
                    },
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      minHeight: 0,
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", flexShrink: 0 }}>
                      <Box sx={{ flex: 1, padding: "8px" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "8px",
                            padding: "4px",
                            alignItems: "flex-start",
                          }}
                        >
                          {!multiSelectEnabled && !isAlreadyRegistered && (
                            <Box
                              sx={{
                                width: "16px",
                                height: "16px",
                                borderRadius: "8px",
                                border:
                                  selectedFacility === facility.site_name
                                    ? "4px solid #065b70"
                                    : "1px solid #065b70",
                                backgroundColor:
                                  selectedFacility === facility.site_name
                                    ? "#065b70"
                                    : "transparent",
                                flexShrink: 0,
                                mt: 0.25,
                              }}
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 500,
                                fontSize: "16px",
                                color: isAlreadyRegistered ? "#94a3b8" : "#065b70",
                                lineHeight: "normal"
                                
                              }}
                            >
                              {facility.site_name} - {facility.city}
                            </Typography>
                            {isAlreadyRegistered && (
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-block",
                                  mt: 0.5,
                                  px: "8px",
                                  py: "2px",
                                  backgroundColor: "#e2e8f0",
                                  color: "#64748b",
                                  borderRadius: "999px",
                                  fontSize: "11px",
                                  fontWeight: 600,
                                  letterSpacing: "0.03em",
                                  lineHeight: "18px",
                                }}
                              >
                                {isOwnFacility
                                  ? "Already registered"
                                  : "Registered by another user"}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          backgroundColor: isAlreadyRegistered ? "#94a3b8" : "#3095ab",
                          borderLeft: `1px solid ${isAlreadyRegistered ? "#cbd5e1" : "#086e86"}`,
                          borderTop: `1px solid ${isAlreadyRegistered ? "#cbd5e1" : "#086e86"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: "8px",
                          alignSelf: "stretch",
                        }}
                      >
                        <Box sx={{ width: "12px", height: "12px" }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="13"
                            viewBox="0 0 12 13"
                            fill="none"
                          >
                            <path
                              d="M1.14645 11.3536C0.951185 11.1583 0.951185 10.8417 1.14645 10.6464L9.29289 2.5H4.46988C4.19374 2.5 3.96988 2.27614 3.96988 2C3.96988 1.72386 4.19374 1.5 4.46988 1.5H10.5C10.7761 1.5 11 1.72386 11 2V8C11 8.27614 10.7761 8.5 10.5 8.5C10.2239 8.5 10 8.27614 10 8V3.20711L1.85355 11.3536C1.65829 11.5488 1.34171 11.5488 1.14645 11.3536Z"
                              fill="white"
                            />
                          </svg>
                        </Box>
                      </Box>
                    </Box>
                    {/* Details: type line (CMS vs healthcare by country), Medicare CCN (if present), NPI, ownership, location */}
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        px: 1.5,
                        pb: 1.5,
                        pt: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        borderTop: "1px solid #e5e7eb",
                        mt: 0.5,
                        mx: 1,
                      }}
                    >
                      <Typography
                        component="div"
                        sx={{ fontSize: "12px", color: "#262626", lineHeight: 1.4 }}
                      >
                        <Box
                          component="span"
                          sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                        >
                          {cmsTypeLabel}
                        </Box>
                        {facilityType || "—"}
                      </Typography>
                      {Boolean(ccn && String(ccn).trim()) && (
                        <Typography
                          component="div"
                          sx={{ fontSize: "12px", color: "#262626", lineHeight: 1.4 }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                          >
                            Medicare CCN:
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              color: "#059669",
                              fontWeight: 600,
                            }}
                          >
                            {ccn}
                          </Box>
                        </Typography>
                      )}
                      {(facility.npi_number ?? (facility as any).npi) && (() => {
                        const primaryNpi = facility.npi_number ?? (facility as any).npi;
                        const altA = facility.alternativeNpis ?? [];
                        const altB = (facility as any).alternative_npi_numbers ?? [];
                        const alternativeNpis: string[] = Array.from(
                          new Set(
                            [...altA, ...altB].filter(
                              (x): x is string => Boolean(x && String(x).trim()),
                            ),
                          ),
                        );
                        const hasAlternatives = alternativeNpis.length > 0;
                        const pt = facility.primary_taxonomy as
                          | { code?: string; description?: string; desc?: string }
                          | undefined;
                        const taxCode =
                          pt?.code?.trim() ||
                          (facility as { taxonomy_code?: string }).taxonomy_code?.trim() ||
                          "—";
                        const taxDescription =
                          (pt?.description || pt?.desc || "").trim() ||
                          (() => {
                            const code = pt?.code?.trim();
                            if (!code || !Array.isArray((facility as any).taxonomies)) return "";
                            const match = (facility as any).taxonomies.find(
                              (t: { code?: string; desc?: string }) =>
                                (t.code || "").trim() === code,
                            );
                            return (match?.desc || "").trim();
                          })() ||
                          "—";
                        const npiRow = (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.25,
                            }}
                          >
                            <Typography
                              component="div"
                              sx={{ fontSize: "12px", color: "#262626", lineHeight: 1.4 }}
                            >
                              <Box
                                component="span"
                                sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                              >
                                NPI:
                              </Box>
                              <Box
                                component="span"
                                sx={{
                                  color: "#059669",
                                  fontWeight: 600,
                                  borderBottom: hasAlternatives ? "1px dashed #059669" : "none",
                                  cursor: hasAlternatives ? "help" : "inherit",
                                }}
                              >
                                {primaryNpi}
                                {hasAlternatives && (
                                  <Box
                                    component="span"
                                    sx={{
                                      color: "#6b7280",
                                      fontWeight: 400,
                                      ml: 0.5,
                                      fontSize: "11px",
                                    }}
                                  >
                                    +{alternativeNpis.length} more
                                  </Box>
                                )}
                              </Box>
                            </Typography>
                            <Typography
                              component="div"
                              sx={{ fontSize: "12px", color: "#262626", lineHeight: 1.4 }}
                            >
                              <Box
                                component="span"
                                sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                              >
                                Code:
                              </Box>
                              <Box
                                component="span"
                                sx={{ color: "#059669", fontWeight: 600 }}
                              >
                                {taxCode}
                              </Box>
                            </Typography>
                            <Typography
                              component="div"
                              sx={{ fontSize: "12px", color: "#262626", lineHeight: 1.4 }}
                            >
                              <Box
                                component="span"
                                sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                              >
                                Type:
                              </Box>
                              {taxDescription}
                            </Typography>
                          </Box>
                        );
                        if (!hasAlternatives) return npiRow;
                        return (
                          <Tooltip
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography sx={{ fontSize: "11px", fontWeight: 700, mb: 0.75, color: "#e2e8f0" }}>
                                  All NPIs at this location:
                                </Typography>
                                {[primaryNpi, ...alternativeNpis].map((npi: string, i: number) => (
                                  <Typography key={i} sx={{ fontSize: "11px", color: i === 0 ? "#6ee7b7" : "#94a3b8" }}>
                                    {npi}{i === 0 ? " (selected)" : ""}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="top"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span>{npiRow}</span>
                          </Tooltip>
                        );
                      })()}
                      {ownership != null && (
                        <Typography
                          component="div"
                          sx={{ fontSize: "12px", color: "#262626", lineHeight: 1.4 }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                          >
                            Ownership:
                          </Box>
                          {ownership}
                        </Typography>
                      )}
                      {isUnitedStatesFacility(facility) && !showAddressBanner && (
                        <Typography
                          component="div"
                          sx={{
                            fontSize: "12px",
                            color: "#262626",
                            lineHeight: 1.4,
                            mt: "auto",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                          >
                            Location:
                          </Box>
                          {locationStr || "—"}
                        </Typography>
                      )}
                      {isUnitedStatesFacility(facility) && showAddressBanner && (
                        <Box sx={{ flex: 1, minHeight: 0 }} aria-hidden />
                      )}
                      {!isUnitedStatesFacility(facility) && (
                        <Typography
                          component="div"
                          sx={{
                            fontSize: "12px",
                            color: "#262626",
                            lineHeight: 1.45,
                            mt: "auto",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{ fontWeight: 600, color: "#065b70", mr: 0.5 }}
                          >
                            Address:
                          </Box>
                          {getNonUsFacilityAddressDisplay(facility) ||
                            locationStr ||
                            "—"}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          mt: 1.5,
                          pt: 1,
                          borderTop: "1px solid #e6f4f7",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isAlreadyRegistered ? (
                          <Typography
                            sx={{ fontSize: 13, color: "#5d7380", fontWeight: 500 }}
                          >
                            {isOwnFacility
                              ? "Already subscribed in your account"
                              : "Registered by another user"}
                          </Typography>
                        ) : inCart ? (
                          <Button
                            type="button"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeCartFacility(cardCartKey);
                            }}
                            sx={{
                              textTransform: "none",
                              bgcolor: "#ccfbf1",
                              color: "#0f766e",
                              border: "1px solid #99f6e4",
                              fontWeight: 600,
                              px: 2,
                              "&:hover": { bgcolor: "#99f6e4" },
                            }}
                          >
                            Remove
                          </Button>
                        ) : multiSelectEnabled ? (
                          <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            disabled={
                              loading ||
                              cartFull ||
                              isAlreadyRegistered ||
                              associationClickInFlightRef.current
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                !loading &&
                                !isAlreadyRegistered &&
                                !associationClickInFlightRef.current
                              ) {
                                void handleFacilityCardClick(facility);
                              }
                            }}
                            sx={{
                              textTransform: "none",
                              borderColor: "#0e7490",
                              color: "#0e7490",
                              fontWeight: 600,
                              px: 2,
                              "&:hover": {
                                borderColor: "#0b4f61",
                                bgcolor: "#f0fdfa",
                              },
                            }}
                          >
                            Add
                          </Button>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            });

            if (showAddressBanner) {
              return (
                <Box
                  key={groupKey}
                  sx={{
                    gridColumn: "1 / -1",
                    borderRadius: "8px",
                    border: "1px solid #bae6fd",
                    backgroundColor: "#f0f9ff",
                    p: 1.5,
                    boxSizing: "border-box",
                  }}
                >
                  <Box>
                    {bannerLocation ? (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        {bannerLocation}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: "24px",
                      width: "100%",
                      mt: 1.5,
                      alignItems: "stretch",
                    }}
                  >
                    {groupCardNodes}
                  </Box>
                </Box>
              );
            }
            return <Fragment key={groupKey}>{groupCardNodes}</Fragment>;
          })}
          </Box>
        </Box>
      </FacilityDropdownSection>
    );
  };

  // Build NPI success message (base API message only).
  const fullNpiSuccessMessage = useMemo(() => {
    return npiSuccessMessage || null;
  }, [npiSuccessMessage]);

  const addFacilityByTypePanel = showAddFacilityByType ? (
    <AddFacilityByTypePanel
      searchName={facilityName}
      searchCity={City}
      searchState={selectedState}
      searchPhone={phoneNumber}
      searchEmail={email}
      states={states}
      existingFacilities={existingUserFacilities}
      cartFacilities={facilityCart}
      resetKey={`${searchTab}|${facilityName}|${selectedState}|${City}|${npiNumber}|${errorTitle}`}
    />
  ) : null;

  return (
    <ContentContainer>
      <ContentWrapper sx={{ pb: facilityCart.length > 0 ? { xs: 14, md: 2 } : 0 }}>
        <MainContent>
          <HeaderSection>
            <HeaderContainer>
              <HeaderTitle>
                <CreateSpaceIcon />
                <Title>My Facility</Title>
              </HeaderTitle>
            </HeaderContainer>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
            <FacilityInfoCard>
              <Box>
                <SectionTitle>Set up your facilities</SectionTitle>
                <SectionSubtitle>
                  Add one or more facilities to get started. Your selection is kept as you search.
                </SectionSubtitle>
                <Box
                  role="tablist"
                  aria-label="Facility search method"
                  sx={{
                    display: "flex",
                    width: "100%",
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #99d4e1",
                    mt: 0.75,
                  }}
                >
                  <Button
                    type="button"
                    role="tab"
                    aria-selected={searchTab === "name"}
                    onClick={() => handleSearchTabChange("name")}
                    sx={{
                      flex: 1,
                      py: 0.65,
                      borderRadius: 0,
                      textTransform: "none",
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "14px",
                      border: "none",
                      boxShadow: "none",
                      ...(searchTab === "name"
                        ? {
                            background:
                              "linear-gradient(to right, #0a829d, #58c2d8)",
                            color: "#e6fafc",
                          }
                        : {
                            bgcolor: "#fdffff",
                            color: "#065b70",
                          }),
                      "&:hover": {
                        boxShadow: "none",
                        ...(searchTab === "name"
                          ? {
                              background:
                                "linear-gradient(to right, #065b70, #0a829d)",
                            }
                          : {
                              bgcolor: "#f0fcff",
                            }),
                      },
                    }}
                  >
                    Search by Name
                  </Button>
                  <Button
                    type="button"
                    role="tab"
                    aria-selected={searchTab === "npi"}
                    onClick={() => handleSearchTabChange("npi")}
                    sx={{
                      flex: 1,
                      py: 0.65,
                      borderRadius: 0,
                      textTransform: "none",
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 600,
                      fontSize: "14px",
                      border: "none",
                      boxShadow: "none",
                      borderLeft: "1px solid #99d4e1",
                      ...(searchTab === "npi"
                        ? {
                            background:
                              "linear-gradient(to right, #0a829d, #58c2d8)",
                            color: "#e6fafc",
                          }
                        : {
                            bgcolor: "#fdffff",
                            color: "#065b70",
                          }),
                      "&:hover": {
                        boxShadow: "none",
                        ...(searchTab === "npi"
                          ? {
                              background:
                                "linear-gradient(to right, #065b70, #0a829d)",
                            }
                          : {
                              bgcolor: "#f0fcff",
                            }),
                      },
                    }}
                  >
                    Search by NPI
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: "auto",
                }}
              >
              {searchTab === "name" ? (
              <>
              <LocationSection>
                <LocationHeader>
                  <LocationIcon />
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "16px",
                      color: "#065b70",
                    }}
                  >
                    Select Location
                  </Typography>
                </LocationHeader>

                <LocationGrid
                  sx={{
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    width: "100%",
                    alignItems: "flex-start",
                  }}
                >
                  <FieldContainer>
                    <Box sx={{ padding: "4px 0", width: "100%" }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#065b70",
                          marginBottom: "4px",
                          lineHeight: 1.5,
                        }}
                      >
                        {stateOrProvinceLabel}{" "}
                        <span style={{ color: "#D13438" }}>*</span>
                      </Typography>
                      <FormControl fullWidth>
                        <Autocomplete<FacilityStateOption, false, false, false>
                          options={stateOptions}
                          value={findFacilityStateOptionByAbbrev(selectedState, stateOptions)}
                          inputValue={stateComboInput}
                          onInputChange={(_e, v, reason) => {
                            if (
                              reason === "input" ||
                              reason === "clear" ||
                              reason === "reset"
                            ) {
                              setStateComboInput(v);
                            }
                          }}
                          onChange={(_e, v) => {
                            if (v && typeof v === "object" && "abbr" in v) {
                              setSelectedState(v.abbr);
                              setStateComboInput(v.label);
                            } else {
                              setSelectedState("");
                              setStateComboInput("");
                            }
                          }}
                          filterOptions={(opts, st) =>
                            filterStateComboOptions(opts, st)
                          }
                          isOptionEqualToValue={(a, b) => a.abbr === b.abbr}
                          getOptionLabel={(o) => o.label}
                          renderOption={(props, option) => (
                            <Box
                              component="li"
                              {...props}
                              key={option.id}
                              sx={{
                                whiteSpace: "nowrap",
                                typography: "body2",
                                fontWeight: 600,
                              }}
                            >
                              {option.label}
                            </Box>
                          )}
                          ListboxProps={{
                            sx: {
                              maxHeight: 280,
                              "& .MuiAutocomplete-option": {
                                py: 0.5,
                                minHeight: "unset",
                                lineHeight: 1.35,
                              },
                            },
                          }}
                          size="small"
                          autoHighlight
                          selectOnFocus
                          handleHomeEndKeys
                          aria-label={
                            stateOrProvinceLabel === "Province"
                              ? "Province or territory"
                              : stateOrProvinceLabel === "Emirate"
                                ? "Emirate"
                                : "State or province"
                          }
                          renderInput={(params) => (
                            <StyledTextField
                              {...params}
                              placeholder={stateLocationPlaceholder}
                              variant="outlined"
                              helperText={
                                statesLoading
                                  ? "Loading state suggestions…"
                                  : undefined
                              }
                              inputProps={{
                                ...params.inputProps,
                                autoComplete: "off",
                                onFocus: (fe: React.FocusEvent<HTMLInputElement>) => {
                                  stateFieldFocusedRef.current = true;
                                  params.inputProps?.onFocus?.(fe);
                                },
                                onBlur: (fe: React.FocusEvent<HTMLInputElement>) => {
                                  stateFieldFocusedRef.current = false;
                                  params.inputProps?.onBlur?.(fe);
                                  const raw = fe.currentTarget.value;
                                  const r = resolveStateAbbrevFromUserInput(
                                    raw,
                                    states,
                                  );
                                  if (r) {
                                    setSelectedState(r);
                                    const opt = findFacilityStateOptionByAbbrev(
                                      r,
                                      stateOptions,
                                    );
                                    setStateComboInput(opt?.label ?? r);
                                  } else if (!raw.trim()) {
                                    setSelectedState("");
                                    setStateComboInput("");
                                  } else {
                                    const keep = findFacilityStateOptionByAbbrev(
                                      selectedState,
                                      stateOptions,
                                    );
                                    setStateComboInput(keep?.label ?? "");
                                  }
                                },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </Box>
                  </FieldContainer>

                  <FieldContainer>
                    <Box sx={{ padding: "4px 0", width: "100%" }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#065b70",
                          marginBottom: "4px",
                          lineHeight: 1.5,
                        }}
                      >
                        City{" "}
                        <Box component="span" sx={{ fontWeight: 400, color: "#6b7280", fontSize: "13px" }}>
                          (Optional)
                        </Box>
                      </Typography>
                      <FormControl fullWidth>
                        <StyledTextField
                          value={City}
                          onChange={handleCityNameChange}
                          placeholder={cityLocationPlaceholder}
                          variant="outlined"
                        />
                      </FormControl>
                    </Box>
                  </FieldContainer>
                </LocationGrid>
              </LocationSection>

              <FacilityNameSection>
                <FieldContainer sx={{ maxWidth: "100%" }}>
                  <FacilityNameHeader>
                    <BuildingIcon />
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#065b70",
                        marginBottom: "4px",
                      }}
                    >
                      Facility Name{" "}
                      <span style={{ color: "#D13438" }}>*</span>
                    </Typography>
                  </FacilityNameHeader>
                  <StyledTextField
                    value={facilityName}
                    onChange={handleFacilityNameChange}
                    placeholder="e.g. Riverdale Rural Health Clinic"
                    variant="outlined"
                  />
                </FieldContainer>
              </FacilityNameSection>
              </>
              ) : (
              <FacilityNameSection>
                <FieldContainer sx={{ maxWidth: "100%" }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "16px",
                      color: "#065b70",
                      marginBottom: "4px",
                    }}
                  >
                    NPI Number{" "}
                    <span style={{ color: "#D13438" }}>*</span>
                  </Typography>
                  <Typography
                    component="div"
                    variant="caption"
                    sx={{ color: "#4f6f78", mb: 0.5, display: "block" }}
                  >
                    Organization (Type 2) NPI only — 10 digits.
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <StyledTextField
                      value={npiNumber}
                      onChange={handleNpiNumberChange}
                      placeholder="Enter 10-digit NPI number"
                      variant="outlined"
                      inputProps={{
                        inputMode: "numeric",
                        maxLength: 10,
                        "aria-label": "10-digit organization NPI",
                      }}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "#4f6f78", mt: 0.5, display: "block" }}
                  >
                    {npiNumber.length}/10 digits
                  </Typography>
                  <Link
                    href={NPPES_REGISTRY_SEARCH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                      mt: 0.5,
                      display: "inline-block",
                      fontSize: "13px",
                      fontWeight: 500,
                      fontFamily: "Rethink Sans, sans-serif",
                      color: "#0a829d",
                    }}
                  >
                    Don&apos;t have it? Look up on NPPES.
                  </Link>
                </FieldContainer>
              </FacilityNameSection>
              )}
              </Box>

              {/* Phone Number and Email Row */}
              <FacilityNameSection>
                <LocationGrid
                  sx={{
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    width: "100%",
                  }}
                >
                  <FieldContainer>
                    <Box sx={{ padding: "4px 0" }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#065b70",
                          marginBottom: "4px",
                        }}
                      >
                        Phone Number{" "}
                        <span style={{ color: "#D13438" }}>*</span>
                      </Typography>
                      <StyledTextField
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        placeholder="e.g. 123-456-7890"
                        variant="outlined"
                        type="tel"
                      />
                    </Box>
                  </FieldContainer>

                  <FieldContainer>
                    <Box sx={{ padding: "4px 0" }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "16px",
                          color: "#065b70",
                          marginBottom: "4px",
                        }}
                      >
                        Email
                      </Typography>
                      <StyledTextField
                        value={email}
                        onChange={handleEmailChange}
                        placeholder="Enter Email"
                        variant="outlined"
                        type="email"
                        disabled
                        InputProps={{
                          readOnly: true,
                          sx: {
                            backgroundColor: "#f0f0f0",
                          },
                        }}
                      />
                    </Box>
                  </FieldContainer>
                </LocationGrid>
              </FacilityNameSection>

              {!isValidated ? (
                <ButtonContainer>
                  {/* <CancelButton onClick={handleCancel}>Cancel</CancelButton> */}
                  <ValidateButton
                    id="validateBtn"
                    onClick={handleValidate}
                    startIcon={
                      loading ? undefined : (
                        <SearchIcon sx={{ fontSize: "20px !important" }} />
                      )
                    }
                    disabled={
                      loading ||
                      !phoneNumber.trim() ||
                      !email.trim() ||
                      (searchTab === "name"
                        ? !facilityName.trim() ||
                          !resolveStateAbbrevForApi(selectedState, states)
                        : npiNumber.length !== 10)
                    }
                  >
                    {loading ? (
                      <CircularProgress size={16} sx={{ color: "#e6fafc" }} />
                    ) : (
                      "Search Facilities"
                    )}
                  </ValidateButton>
                </ButtonContainer>
              ) : null}

              {/* Container used by legacy facility-context.js for NPI messages */}
              <Box
                id="validationResults"
                sx={{
                  display: "none",
                  marginTop: "12px",
                  width: "100%",
                }}
              />

              {/* Legacy API success result display (hidden for NPI-driven results) */}
              {isValidated &&
                validationResult &&
                validationResult.success &&
                validationResult.is_valid === true && (
                  <>
                    {/* Show success message only when at least one facility card is shown (not when filters hide every API row). */}
                    {!validationFromNpi &&
                      !npiData &&
                      displayedValidationFacilities.length > 0 && (
                      <SuccessContainer>
                        <SuccessHeader>
                          <SuccessIcon />
                          <SuccessTitle>Facility Found!</SuccessTitle>
                        </SuccessHeader>
                        <SuccessMessage>
                          We found your facility and related information. Please
                          select the most appropriate option below:
                        </SuccessMessage>
                      </SuccessContainer>
                    )}

                    {/* Show NPI success message for NPI lookup */}
                    {npiData &&
                      fullNpiSuccessMessage &&
                      displayedValidationFacilities.length > 0 && (
                      <SuccessContainer>
                        <SuccessHeader>
                          <SuccessIcon />
                          <SuccessTitle>Facility Found!</SuccessTitle>
                        </SuccessHeader>
                        <SuccessMessage>{fullNpiSuccessMessage}</SuccessMessage>
                        {/* If facility_id (via facility_type.id) is missing but we have a primary taxonomy code,
                            show an extra warning line in red */}
                        {!(
                          (npiData as any).facility_type &&
                          (npiData as any).facility_type.id
                        ) &&
                          (npiData as any).primary_taxonomy?.code && (
                            <Typography
                              sx={{
                                mt: 0.5,
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 400,
                                fontSize: "14px",
                                color: "#dc2626",
                              }}
                            >
                              {`Facility type not found for this code ${
                                (npiData as any).primary_taxonomy.code
                              }.`}
                            </Typography>
                          )}
                      </SuccessContainer>
                    )}

                    {/* One card per API row; client filters by state/city/name when provided; NPI-2 only. */}
                    {Array.isArray(validationResult?.facilities) &&
                    validationResult.facilities.length > 0 &&
                    displayedValidationFacilities.length === 0 ? (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          borderRadius: "8px",
                          border: "1px solid #99d4e1",
                          backgroundColor: "#f0fcff",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {(() => {
                            if (regionFilteredValidationFacilities.length === 0) {
                              const c = City.trim();
                              const n = facilityName.trim();
                              if (c && n) {
                                return (
                                  <>
                                    No facilities in this list match your practice
                                    location city (&ldquo;{c}&rdquo;) and facility
                                    name (&ldquo;{n}&rdquo;).
                                  </>
                                );
                              }
                              if (c) {
                                return (
                                  <>
                                    No facilities in this list have practice
                                    location city &ldquo;{c}&rdquo;.
                                  </>
                                );
                              }
                              return (
                                <>
                                  No facilities in this list match the facility name
                                  &ldquo;{n}&rdquo;.
                                </>
                              );
                            }
                            return <>{facilityMatchFootnoteLines.type2FilteredLine}</>;
                          })()}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#4f6f78",
                            fontFamily: "Rethink Sans, sans-serif",
                            mt: 1,
                            lineHeight: 1.5,
                          }}
                        >
                          {facilityMatchFootnoteLines.facilities}
                        </Typography>
                        {addFacilityByTypePanel}
                      </Box>
                    ) : (
                      renderFacilityCards(displayedValidationFacilities)
                    )}

                    {/* When facility type not confirmed and no type determined, require user to select Provider Type for penalty/compliance */}
                    {(() => {
                        const chosen = getSelectedFacility(validationResult);
                        const hasDeterminedType = !!(
                          ((chosen as any)?.facility_type_slug ?? "").trim() ||
                          ((chosen as any)?.facility_type ?? "").toString().trim()
                        );
                        if (
                          !chosen ||
                          chosen.facility_type_confirmed === true ||
                          hasDeterminedType
                        )
                          return null;
                        const requiresSelection = (chosen as any)?.facility_type_requires_selection === true;
                        const primaryTax = (chosen as any)?.primary_taxonomy;
                        const taxonomyDesc =
                          primaryTax?.description ?? primaryTax?.desc ?? primaryTax?.code ?? "";
                        const taxonomyCode = primaryTax?.code ?? "";
                        const npiNum = (chosen as any)?.npi_number ?? "";
                        const cityState = [
                          (chosen as any)?.city ?? (chosen as any)?.primary_address?.city,
                          (chosen as any)?.state ?? (chosen as any)?.primary_address?.state,
                        ]
                          .filter(Boolean)
                          .join(", ");
                        const defaultSlug =
                          (chosen as any)?.default_facility_type_slug ||
                          chosen.facility_type_slug ||
                          (typeof chosen.facility_type === "string" ? chosen.facility_type : "") ||
                          "";
                        const selectValue =
                          (chosen.facility_type_slug ||
                            (typeof chosen.facility_type === "string" ? chosen.facility_type : "")) ||
                          defaultSlug;
                        return (
                          <Box sx={{ mt: 2, maxWidth: 480 }}>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#065b70",
                                mb: 1,
                              }}
                            >
                              {requiresSelection && (taxonomyCode || taxonomyDesc) ? (
                                <>
                                  {(chosen as any)?.site_name ?? (chosen as any)?.organization_name}
                                  {cityState ? ` – ${cityState}` : ""}
                                  {npiNum ? `, NPI ${npiNum}` : ""} is registered with: Primary
                                  taxonomy: {taxonomyCode}
                                  {taxonomyDesc && taxonomyDesc !== taxonomyCode
                                    ? ` (${taxonomyDesc})`
                                    : ""}
                                  . Since these are physician practices, please select a primary
                                  facility type:
                                </>
                              ) : (
                                <>
                                  Select Provider Type for accurate penalty and compliance analysis
                                </>
                              )}
                            </Typography>
                            <FormControl fullWidth size="small">
                              <Select
                                displayEmpty
                                value={selectValue}
                                onChange={(e: SelectChangeEvent<string>) => {
                                  const slug = e.target.value;
                                  if (!slug) return;
                                  const opt = providerTypeOptions.find(
                                    (o) => o.slug === slug
                                  );
                                  if (!opt) return;
                                  const selectedName = chosen.site_name;
                                  setValidationResult((prev: any) => {
                                    if (!prev || !prev.facilities) return prev;
                                    return {
                                      ...prev,
                                      facilities: prev.facilities.map(
                                        (f: any) =>
                                          f.site_name === selectedName
                                            ? {
                                                ...f,
                                                facility_type: opt.display_name,
                                                facility_type_slug: opt.slug,
                                                facility_type_confirmed: true,
                                              }
                                            : f
                                      ),
                                    };
                                  });
                                }}
                                sx={{ backgroundColor: "#fff" }}
                              >
                                <MenuItem value="">
                                  {providerTypeOptionsLoading
                                    ? "Loading..."
                                    : "Select Provider Type..."}
                                </MenuItem>
                                {providerTypeOptions.map((o) => (
                                  <MenuItem key={o.slug} value={o.slug}>
                                    {o.display_name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        );
                      })()}
                  </>
                )}

              {/* Failure Result Display */}
              {isValidated &&
                validationResult &&
                validationResult.success &&
                validationResult.is_valid === false && (
                  <>
                    {/*  <FailureContainer>
                      <FailureHeader>
                        <WarningIcon />
                        <FailureTitle>Facility Not Found!</FailureTitle>
                      </FailureHeader>
                      <FailureMessage>
                        We couldn't find your facility information. Below are
                        the suggestions based on available location information.
                      </FailureMessage>
                    </FailureContainer>*/}

                    <SuccessContainer>
                      <SuccessHeader>
                        <SuccessIcon />
                        <SuccessTitle>Facility Suggestions!</SuccessTitle>
                      </SuccessHeader>
                      <SuccessMessage>
                        Below are the suggestions based on available location
                        information.
                      </SuccessMessage>
                    </SuccessContainer>

                    <FacilityDropdownSection>
                      <FacilityDropdownHeader>
                        <BuildingIcon />
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: "16px",
                            color: "#065b70",
                          }}
                        >
                          Suggested Facility Names
                        </Typography>
                      </FacilityDropdownHeader>

                      <Box sx={{ padding: "8px 0" }}>
                        {Array.isArray(validationResult?.suggestions) &&
                        validationResult.suggestions.length > 0 &&
                        displayedSuggestionFacilities.length === 0 ? (
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "8px",
                              border: "1px solid #99d4e1",
                              backgroundColor: "#f0fcff",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: "#065b70",
                                fontFamily: "Rethink Sans, sans-serif",
                              }}
                            >
                              {(() => {
                                if (regionFilteredSuggestionFacilities.length === 0) {
                                  const c = City.trim();
                                  const n = facilityName.trim();
                                  if (c && n) {
                                    return (
                                      <>
                                        No suggestions in this list match your
                                        practice location city (&ldquo;{c}&rdquo;)
                                        and facility name (&ldquo;{n}&rdquo;).
                                      </>
                                    );
                                  }
                                  if (c) {
                                    return (
                                      <>
                                        No suggestions in this list have practice
                                        location city &ldquo;{c}&rdquo;.
                                      </>
                                    );
                                  }
                                  return (
                                    <>
                                      No suggestions in this list match the
                                      facility name &ldquo;{n}&rdquo;.
                                    </>
                                  );
                                }
                                return <>{facilityMatchFootnoteLines.type2FilteredLine}</>;
                              })()}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#4f6f78",
                                fontFamily: "Rethink Sans, sans-serif",
                                mt: 1,
                                lineHeight: 1.5,
                              }}
                            >
                              {facilityMatchFootnoteLines.suggestions}
                            </Typography>
                          </Box>
                        ) : (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, 1fr)",
                            gap: "24px",
                            width: "100%",
                          }}
                        >
                          {displayedSuggestionFacilities.map(
                            (facility, index) => {
                              const isOwnFacility = isOwnedByCurrentUser(
                                facility,
                                ownedFacilityKeys,
                              );
                              const isAlreadyRegistered =
                                isFacilityAlreadyRegistered(
                                  facility,
                                  ownedFacilityKeys,
                                );
                              const suggestionIds = facility as FacilitySuggestion & {
                                npi_number?: string;
                                npi?: string;
                                ccn_number?: string;
                              };
                              const cardCartKey = facilityCartKeyFromDetails({
                                ccn_number:
                                  String(
                                    suggestionIds.npi_number ??
                                      suggestionIds.npi ??
                                      suggestionIds.ccn_number ??
                                      "",
                                  ) || undefined,
                                facility_name: facility.site_name || undefined,
                                city: facility.city || undefined,
                                state: facility.state || undefined,
                              });
                              const inCart = facilityCart.some(
                                (c) => c.cart_key === cardCartKey,
                              );
                              const cartFull = isSelfServeCartFull(
                                existingActiveFacilityCount,
                                facilityCart.length,
                                multiSelectEnabled,
                                userIsSalesUser(),
                              );
                              return (
                              <Box
                                key={index}
                                onClick={() => {
                                  if (
                                    isAlreadyRegistered ||
                                    loading ||
                                    associationClickInFlightRef.current
                                  ) {
                                    return;
                                  }
                                  if (!multiSelectEnabled) {
                                    void handleFacilityCardClick(facility);
                                  }
                                }}
                                sx={{
                                  backgroundColor: isAlreadyRegistered
                                    ? "#f8fafc"
                                    : inCart
                                      ? "#f0fdfa"
                                      : "#ffffff",
                                  border: isAlreadyRegistered
                                    ? "1px solid #cbd5e1"
                                    : inCart
                                      ? "2px solid #0e7490"
                                      : "1px solid #065b70",
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  cursor: isAlreadyRegistered
                                    ? "not-allowed"
                                    : loading
                                      ? "not-allowed"
                                      : multiSelectEnabled
                                        ? "default"
                                        : "pointer",
                                  opacity: isAlreadyRegistered
                                    ? 0.55
                                    : loading
                                      ? 0.6
                                      : 1,
                                  boxShadow: isAlreadyRegistered
                                    ? "none"
                                    : "0px 4px 4px 0px rgba(0,0,0,0.05)",
                                  "&:hover": {
                                    backgroundColor: isAlreadyRegistered
                                      ? "#f8fafc"
                                      : inCart
                                        ? "#f0fdfa"
                                        : loading
                                          ? "#ffffff"
                                          : "#f5f5f5",
                                  },
                                }}
                              >
                                <Box sx={{ display: "flex", height: "100%" }}>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      padding: "8px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: "8px",
                                        alignItems: "center",
                                        padding: "4px",
                                      }}
                                    >
                                      {!multiSelectEnabled &&
                                        !isAlreadyRegistered && (
                                        <Box
                                          sx={{
                                            width: "16px",
                                            height: "16px",
                                            borderRadius: "8px",
                                            border:
                                              selectedFacility ===
                                              facility.site_name
                                                ? "4px solid #065b70"
                                                : "1px solid #065b70",
                                            backgroundColor:
                                              selectedFacility ===
                                              facility.site_name
                                                ? "#065b70"
                                                : "transparent",
                                            flexShrink: 0,
                                          }}
                                        />
                                      )}
                                      <Typography
                                        sx={{
                                          fontFamily:
                                            "Rethink Sans, sans-serif",
                                          fontWeight: 500,
                                          fontSize: "16px",
                                          color: isAlreadyRegistered
                                            ? "#94a3b8"
                                            : "#065b70",
                                          lineHeight: "normal",
                                          flex: 1,
                                        }}
                                      >
                                        {facility.site_name} - {facility.city}
                                        {isAlreadyRegistered && (
                                          <Box
                                            component="span"
                                            sx={{
                                              display: "inline-block",
                                              ml: 1,
                                              px: "8px",
                                              py: "2px",
                                              backgroundColor: "#e2e8f0",
                                              color: "#64748b",
                                              borderRadius: "999px",
                                              fontSize: "11px",
                                              fontWeight: 600,
                                              letterSpacing: "0.03em",
                                              lineHeight: "18px",
                                            }}
                                          >
                                            {isOwnFacility
                                              ? "Already registered"
                                              : "Registered by another user"}
                                          </Box>
                                        )}
                                        <br />
                                        <span
                                          style={{
                                            fontSize: "14px" /* smaller font */,
                                            fontStyle: "italic",
                                            fontWeight: 300,
                                          }}
                                        >
                                          {facility.facility_type}
                                        </span>
                                        {/* Show address if available */}
                                        {facility.primary_address
                                          ?.address_1 && (
                                          <>
                                            <br />
                                            <span
                                              style={{
                                                fontSize: "12px",
                                                fontWeight: 400,
                                                color: "#666",
                                              }}
                                            >
                                              {
                                                facility.primary_address
                                                  .address_1
                                              }
                                              {facility.primary_address
                                                .address_2 &&
                                                `, ${facility.primary_address.address_2}`}
                                              {facility.primary_address
                                                .postal_code &&
                                                ` ${facility.primary_address.postal_code}`}
                                            </span>
                                          </>
                                        )}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        mt: 1,
                                        pt: 1,
                                        borderTop: "1px solid #e6f4f7",
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {isAlreadyRegistered ? (
                                        <Typography
                                          sx={{
                                            fontSize: 13,
                                            color: "#5d7380",
                                            fontWeight: 500,
                                          }}
                                        >
                                          {isOwnFacility
                                            ? "Already subscribed in your account"
                                            : "Registered by another user"}
                                        </Typography>
                                      ) : inCart ? (
                                        <Button
                                          type="button"
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeCartFacility(cardCartKey);
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            bgcolor: "#ccfbf1",
                                            color: "#0f766e",
                                            border: "1px solid #99f6e4",
                                            fontWeight: 600,
                                            px: 2,
                                          }}
                                        >
                                          Remove
                                        </Button>
                                      ) : multiSelectEnabled ? (
                                        <Button
                                          type="button"
                                          size="small"
                                          variant="outlined"
                                          disabled={
                                            loading ||
                                            cartFull ||
                                            isAlreadyRegistered ||
                                            associationClickInFlightRef.current
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                              !loading &&
                                              !isAlreadyRegistered &&
                                              !associationClickInFlightRef.current
                                            ) {
                                              void handleFacilityCardClick(
                                                facility,
                                              );
                                            }
                                          }}
                                          sx={{
                                            textTransform: "none",
                                            borderColor: "#0e7490",
                                            color: "#0e7490",
                                            fontWeight: 600,
                                            px: 2,
                                          }}
                                        >
                                          Add
                                        </Button>
                                      ) : null}
                                    </Box>
                                  </Box>
                                  <Box
                                    sx={{
                                      backgroundColor: "#3095ab",
                                      borderLeft: "1px solid #086e86",
                                      borderTop: "1px solid #086e86",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      padding: "8px",
                                      alignSelf: "stretch",
                                    }}
                                  >
                                    <Box sx={{ width: "12px", height: "12px" }}>
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                      >
                                        <path
                                          d="M1.14645 11.3536C0.951185 11.1583 0.951185 10.8417 1.14645 10.6464L9.29289 2.5H4.46988C4.19374 2.5 3.96988 2.27614 3.96988 2C3.96988 1.72386 4.19374 1.5 4.46988 1.5H10.5C10.7761 1.5 11 1.72386 11 2V8C11 8.27614 10.7761 8.5 10.5 8.5C10.2239 8.5 10 8.27614 10 8V3.20711L1.85355 11.3536C1.65829 11.5488 1.34171 11.5488 1.14645 11.3536Z"
                                          fill="white"
                                        />
                                      </svg>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                              );
                            },
                          )}
                        </Box>
                        )}
                      </Box>
                    </FacilityDropdownSection>
                  </>
                )}
              {isValidated && validationResult && validationResult.success && (
                <ActionButtonsContainer>
                  <CancelButton onClick={handleCancel}>Cancel</CancelButton>
                  {/* <ContinueButton
                    onClick={handleContinue}
                    disabled={!selectedFacility}
                  >
                    Continue
                  </ContinueButton> */}
                </ActionButtonsContainer>
              )}
              {(error || npiError) && !isValidated && (
                <ErrorContainer>
                  <ErrorHeader>
                    <ErrorIcon>!</ErrorIcon>
                    <ErrorTitle>{errorTitle ?? "Validation Error"}</ErrorTitle>
                  </ErrorHeader>
                  <Box
                    sx={{
                      height: "1px",
                      backgroundColor: "#fca5a5",
                      margin: "4px 0 6px 0",
                    }}
                  />
                  <ErrorMessage>{npiError || error}</ErrorMessage>
                </ErrorContainer>
              )}
              {(error || npiError) && !isValidated ? addFacilityByTypePanel : null}
            </FacilityInfoCard>
              </Box>
              <Box
                component="aside"
                aria-label="Selected facilities"
                sx={{
                  display: { xs: "none", md: "flex" },
                  flexDirection: "column",
                  width: 280,
                  flexShrink: 0,
                  position: "sticky",
                  top: 16,
                  alignSelf: "flex-start",
                  border: "1px solid #cfe6ec",
                  borderRadius: "12px",
                  bgcolor: "#fff",
                  p: 2,
                  gap: 1,
                  maxHeight: "calc(100vh - 120px)",
                  overflow: "auto",
                  boxShadow: "0px 4px 16px 0px rgba(5,89,108,0.08)",
                }}
              >
                <Typography
                  sx={{ fontWeight: 600, color: "#0b4f61", fontSize: 16 }}
                >
                  {facilityCart.length
                    ? `Selected facilities (${facilityCart.length})`
                    : "Selected facilities"}
                </Typography>
                {facilityCart.length === 0 ? (
                  <Typography sx={{ color: "#5d7380", fontSize: 14 }}>
                    Facilities you add will appear here.
                  </Typography>
                ) : (
                  <Box
                    component="ul"
                    sx={{ listStyle: "none", m: 0, p: 0, flex: 1 }}
                  >
                    {facilityCart.map((f) => (
                      <Box
                        component="li"
                        key={f.cart_key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 1,
                          alignItems: "flex-start",
                          py: 1,
                          borderBottom: "1px solid #e6f4f7",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{ fontWeight: 500, color: "#0b4f61", fontSize: 14 }}
                          >
                            {f.facility_name}
                          </Typography>
                          <Typography sx={{ color: "#5d7380", fontSize: 12 }}>
                            {[f.facility_type, f.city].filter(Boolean).join(", ")}
                          </Typography>
                        </Box>
                        <Button
                          type="button"
                          size="small"
                          onClick={() => {
                            removeCartFacility(f.cart_key);
                          }}
                          sx={{
                            minWidth: 28,
                            px: 0.5,
                            color: "#5d7380",
                            textTransform: "none",
                          }}
                          aria-label={`Remove ${f.facility_name}`}
                        >
                          ×
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}
                {!userIsSalesUser() &&
                  facilityCart.length > 0 &&
                  remainingCartSlots(
                    existingActiveFacilityCount,
                    facilityCart.length,
                    multiFacilityUnlocked,
                  ) <= 3 && (
                    <Typography sx={{ color: "#5d7380", fontSize: 12, mt: 1 }}>
                      {remainingCartSlots(
                        existingActiveFacilityCount,
                        facilityCart.length,
                        multiFacilityUnlocked,
                      ) === 0
                        ? multiFacilityUnlocked
                          ? `Limit reached (${cartCap} per account).`
                          : "Active trial allows only one facility."
                        : `${remainingCartSlots(
                            existingActiveFacilityCount,
                            facilityCart.length,
                            multiFacilityUnlocked,
                          )} more can be added.`}
                    </Typography>
                  )}
                <Button
                  variant="contained"
                  disabled={facilityCart.length === 0 || loading}
                  onClick={() => {
                    flushSync(() => {
                      setLoading(true);
                      setLoadingMessage("adding");
                    });
                    navigate("/dashboard/facilities", {
                      state: {
                        currentView: "orderSummary",
                        fromFacilityValidation: true,
                      },
                    });
                  }}
                  sx={{
                    mt: 1.5,
                    bgcolor: "#0e7490",
                    "&:hover": { bgcolor: "#0b4f61" },
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Review order
                </Button>
              </Box>
            </Box>
            <Typography
              component="p"
              align="center"
              sx={{
                width: "100%",
                maxWidth: "1052px",
                mt: 0.5,
                px: 1,
                fontSize: "12px",
                color: "#6b7280",
                fontFamily: "Rethink Sans, sans-serif",
              }}
            >
              {facilityValidationSupportDisclaimer(addFacilityByTypeEnabled)}
            </Typography>
          </HeaderSection>
        </MainContent>
      </ContentWrapper>
      <Dialog
        open={associationDialogOpen}
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          setAssociationDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          },
        }}
      >
        <DialogTitle sx={{ color: "#d32f2f", fontWeight: 600 }}>
          {associationDialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "#262626", fontSize: "14px" }}>
            {associationDialogMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAssociationDialogOpen(false);
              setAssociationDialogTitle("Facility already set up");
            }}
            variant="contained"
            sx={{
              backgroundColor: "#065b70",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#054a5a",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <VerificationInProgressDialog
        open={verificationDialogOpen}
        onClose={() => setVerificationDialogOpen(false)}
        hasExistingFacilities={hasExistingFacilities}
        onOk={() => {
          setVerificationDialogOpen(false);
          handleCancel();
        }}
        onContactSupport={() => {
          setContactSupportOpen(true);
        }}
        showContactSupportButton
        dialogConfig={{
          maxWidth: false,
          fullWidth: true,
          paperSx: {
            width: "560px",
            height: "300px",
            "@media (max-width: 640px)": {
              width: "90vw",
              height: "auto",
            },
          },
        }}
      />

      <ContactSupportDialog
        open={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
      />

      {loading && loadingMessage === "facilities" && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            boxSizing: "border-box",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress size={48} sx={{ color: "#0a829d" }} />
          <Typography sx={{ color: "#737373", fontSize: 18, fontWeight: 500 }}>
            Finding matching facilities…
          </Typography>
        </Box>
      )}
      {facilityCart.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            bgcolor: "#fff",
            borderTop: "1px solid #cfe6ec",
            boxShadow: "0 -6px 24px rgba(11, 79, 97, 0.08)",
            px: { xs: 2, md: 3 },
            py: 1.5,
            display: { md: "none" },
          }}
        >
          <Box
            sx={{
              maxWidth: 1040,
              mx: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography fontWeight={600} color="#0b4f61">
                {facilityCart.length}{" "}
                {facilityCart.length === 1 ? "facility" : "facilities"} added
              </Typography>
              {!userIsSalesUser() && (
              <Typography color="#5d7380" fontSize={14}>
                {remainingCartSlots(
                  existingActiveFacilityCount,
                  facilityCart.length,
                  multiFacilityUnlocked,
                ) === 0
                  ? multiFacilityUnlocked
                    ? "Limit reached"
                    : "Active trial allows only one facility"
                  : `${remainingCartSlots(
                      existingActiveFacilityCount,
                      facilityCart.length,
                      multiFacilityUnlocked,
                    )} more can be added (max ${cartCap})`}
              </Typography>
              )}
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  m: 0,
                  p: 0,
                  mt: 0.5,
                  maxHeight: 96,
                  overflow: "auto",
                }}
              >
                {facilityCart.map((f) => (
                  <Box
                    component="li"
                    key={f.cart_key}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 1,
                      py: 0.25,
                    }}
                  >
                    <Typography fontSize={13} color="#0b4f61" noWrap>
                      {f.facility_name}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        removeCartFacility(f.cart_key);
                      }}
                      sx={{ textTransform: "none", color: "#0e7490", minWidth: 0 }}
                    >
                      Remove
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
            <Button
              variant="contained"
              disabled={loading}
              onClick={() => {
                flushSync(() => {
                  setLoading(true);
                  setLoadingMessage("adding");
                });
                navigate("/dashboard/facilities", {
                  state: {
                    currentView: "orderSummary",
                    fromFacilityValidation: true,
                  },
                });
              }}
              sx={{
                bgcolor: "#0e7490",
                "&:hover": { bgcolor: "#0b4f61" },
                textTransform: "none",
                px: 3,
                fontWeight: 600,
              }}
            >
              Review order
            </Button>
          </Box>
        </Box>
      )}

      <AssociationVerificationOverlay
        open={
          loading &&
          (loadingMessage === "plans" || loadingMessage === "adding")
        }
        title={
          loadingMessage === "adding" ? "Loading…" : "Verification in Progress"
        }
        subtitle={
          loadingMessage === "adding" ? undefined : "Verification pending"
        }
      />
    </ContentContainer>
  );
}
